import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft, Upload, Play, Download, Loader2, CheckCircle, XCircle,
  Clock, Sparkles, ChevronRight, ChevronLeft, X, RefreshCw, Image as ImageIcon,
  FileText, ChevronDown, Link2, Beaker, Zap, Lock, Unlock,
  Palette, Camera, Users, Sun, Film, PenTool, Move, Wrench,
  History, FileVideo, AlertTriangle, Copy, Heart, Wand2, Maximize2, FolderOpen
} from 'lucide-react';
import { toast } from '../utils/toast';
import RecipeAssistant from '../components/RecipeAssistant';
import SeedanceJobStatus from '../components/SeedanceJobStatus';
import MontageVideoInputs from '../components/MontageVideoInputs';
import MontageOutputView from '../components/MontageOutputView';
import MontageTimelinePanel from '../components/MontageTimelinePanel';
import URLtoAdInputs from '../components/URLtoAdInputs';
import SeedanceDirectInputs from '../components/SeedanceDirectInputs';
import Seedance25DirectInputs from '../components/Seedance25DirectInputs';
import HappyHorseDirectInputs from '../components/HappyHorseDirectInputs';
import KlingDirectInputs from '../components/KlingDirectInputs';
import SketchSamModal from '../components/SketchSamModal';
import GeminiIterationModal from '../components/GeminiIterationModal';
import AIPhotoshopGalleryPanel from '../components/AIPhotoshopGalleryPanel';
import VLMQCPanel from '../components/VLMQCPanel';
import ScriptNormalizerOutput from '../components/ScriptNormalizerOutput';
import ImagePickerModal from '../components/ImagePickerModal';
import TVCPipelineView from '../components/TVCPipelineView';

const API = window.__API_BASE;

// Debounce 401 toast to avoid flooding
let last401Toast = 0;
const show401Toast = () => {
  const now = Date.now();
  if (now - last401Toast > 10000) { // Only show once per 10 seconds
    last401Toast = now;
    toast.error('Session expired - please reload the page');
  }
};

// Authenticated file download - fetches with Bearer token, triggers browser download
const authDownload = async (url, filename) => {
  const token = localStorage.getItem('token');
  // Determine if this is our own API URL or truly external (CDN, etc.)
  const isOwnApi = url.includes('/api/recipes/storage/') || (API && url.startsWith(API));
  const isExternalUrl = (url.startsWith('http://') || url.startsWith('https://')) && !isOwnApi;
  const headers = isExternalUrl ? {} : { Authorization: `Bearer ${token}` };
  
  try {
    const res = await fetch(url, { headers });
    if (res.status === 401) {
      show401Toast();
      throw new Error('Session expired');
    }
    if (!res.ok) throw new Error(`Download failed: ${res.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || url.split('/').pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('Download error:', err);
    if (!err.message.includes('Session expired')) {
      toast.error('Download failed - please try again');
    }
  }
};

// Authenticated image component - fetches image with Bearer token
function AuthImage({ src, alt, className, onClick, style }) {
  const [blobUrl, setBlobUrl] = React.useState(null);
  const [error, setError] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState('');
  const blobRef = React.useRef(null);

  // Direct-render branch for sources that don't need auth:
  //  - data: URLs (already in memory)
  //  - truly external public URLs (S3 / CDN) where a browser-native <img>
  //    load sidesteps CORS entirely. Using fetch() here triggers CORS
  //    preflights that S3 buckets don't advertise, causing spurious
  //    "Failed to load" + "Request object could not be cloned" errors
  //    inside sandboxed runtimes.
  const isOwnApi = src && (src.includes('/api/recipes/storage/') || src.includes('/api/storage/') || (API && src.startsWith(API)));
  const isExternalPublic = src && (src.startsWith('http://') || src.startsWith('https://')) && !isOwnApi;
  const isDataUrl = src && src.startsWith('data:');
  const directRender = isDataUrl || isExternalPublic;

  React.useEffect(() => {
    if (!src || directRender) return;
    let cancelled = false;
    setError(false);
    setErrorMsg('');
    setBlobUrl(null);
    const token = localStorage.getItem('token');
    
    // Revoke previous blob before fetching new one
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }

    const headers = { Authorization: `Bearer ${token}` };

    fetch(src, { headers })
      .then(r => {
        if (r.status === 401) {
          show401Toast();
          throw new Error('Session expired (401)');
        }
        if (r.status === 403) throw new Error('Access denied (403)');
        if (r.status === 404) throw new Error('File not found (404)');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        if (!cancelled) {
          if (blob.size === 0) throw new Error('Empty file');
          const url = URL.createObjectURL(blob);
          blobRef.current = url;
          setBlobUrl(url);
        }
      })
      .catch((err) => {
        console.error('AuthImage fetch error:', src, err.message);
        if (!cancelled) {
          setError(true);
          setErrorMsg(err.message);
        }
      });

    return () => {
      cancelled = true;
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, [src, directRender]);

  // Data URLs and public external URLs render straight through — no auth,
  // no fetch, no CORS preflight.
  if (directRender) {
    return <img src={src} alt={alt} className={className} onClick={onClick} style={style} />;
  }

  if (error) return (
    <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', flexDirection: 'column',
      justifyContent: 'center', background: 'rgba(255,255,255,0.03)', minHeight: '100px', gap: '8px' }}>
      <XCircle className="w-5 h-5 text-red-400/40" />
      <span className="text-[9px] text-red-400/60">{errorMsg || 'Failed to load'}</span>
      <button 
        onClick={() => { setError(false); setBlobUrl(null); setErrorMsg(''); }} 
        className="text-[10px] text-[#0EA5E9]/70 hover:text-[#0EA5E9] underline transition-colors"
        title="Click to re-fetch this image from the server"
      >
        Tap to retry
      </button>
    </div>
  );

  if (!blobUrl) return (
    <div className={className} style={{ ...style, display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'rgba(255,255,255,0.03)', minHeight: '100px' }}>
      <Loader2 className="w-5 h-5 animate-spin text-white/20" />
    </div>
  );

  return <img src={blobUrl} alt={alt} className={className} onClick={onClick} style={style} />;
}

// Authenticated video component - fetches video with Bearer token
function AuthVideo({ src, className, controls }) {
  const [blobUrl, setBlobUrl] = React.useState(null);
  const [error, setError] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState('');
  const blobRef = React.useRef(null);

  React.useEffect(() => {
    if (!src) return;
    let cancelled = false;
    setError(false);
    setErrorMsg('');
    setBlobUrl(null);
    const token = localStorage.getItem('token');
    
    // Revoke previous blob before fetching new one
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }
    
    // Determine if this is our own API URL or truly external
    const isOwnApi = src.includes('/api/recipes/storage/') || (API && src.startsWith(API));
    const isExternalUrl = (src.startsWith('http://') || src.startsWith('https://')) && !isOwnApi;
    const headers = isExternalUrl ? {} : { Authorization: `Bearer ${token}` };
    
    fetch(src, { headers })
      .then(r => {
        if (r.status === 401) {
          show401Toast();
          throw new Error('Session expired (401)');
        }
        if (r.status === 403) throw new Error('Access denied (403)');
        if (r.status === 404) throw new Error('File not found (404)');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        if (!cancelled) {
          if (blob.size === 0) throw new Error('Empty file');
          const url = URL.createObjectURL(blob);
          blobRef.current = url;
          setBlobUrl(url);
        }
      })
      .catch((err) => {
        console.error('AuthVideo fetch error:', src, err.message);
        if (!cancelled) {
          setError(true);
          setErrorMsg(err.message);
        }
      });

    return () => {
      cancelled = true;
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, [src]);

  if (error) return (
    <div className={className} style={{ display: 'flex', alignItems: 'center', flexDirection: 'column',
      justifyContent: 'center', background: 'black', minHeight: '100px', gap: '8px' }}>
      <XCircle className="w-5 h-5 text-red-400/40" />
      <span className="text-[9px] text-red-400/60">{errorMsg || 'Failed to load'}</span>
      <button onClick={() => { setError(false); setBlobUrl(null); setErrorMsg(''); }} className="text-[10px] text-white/40 underline">Retry</button>
    </div>
  );

  if (!blobUrl) return (
    <div className={className} style={{ display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'black', minHeight: '100px' }}>
      <Loader2 className="w-5 h-5 animate-spin text-white/20" />
    </div>
  );

  return <video src={blobUrl} className={className} controls={controls} />;
}

// Color grade style preview data — short mood descriptions + color swatches
const COLOR_GRADE_HINTS = {
  "Blade Runner": { colors: ["#1a1a3e", "#ff6b00", "#00b4d8"], hint: "Neon-soaked noir" },
  "Wes Anderson": { colors: ["#f4c2a1", "#e07b5a", "#8db596"], hint: "Pastel symmetry" },
  "Christopher Nolan": { colors: ["#1c2833", "#5d6d7e", "#aeb6bf"], hint: "Cool, desaturated" },
  "David Fincher": { colors: ["#1b2631", "#2e4053", "#7fb3d8"], hint: "Dark, greenish teal" },
  "Wong Kar-wai": { colors: ["#c0392b", "#f1c40f", "#1a5276"], hint: "Saturated, moody" },
  "Film Noir": { colors: ["#0d0d0d", "#4a4a4a", "#c0c0c0"], hint: "High contrast B&W" },
  "Teal & Orange": { colors: ["#008080", "#ff8c00", "#1a1a2e"], hint: "Blockbuster pop" },
  "Desaturated War Film": { colors: ["#6b7b6b", "#4a4a3a", "#8b8b7a"], hint: "Muted, gritty" },
  "The Matrix (Green Tint)": { colors: ["#003300", "#00ff41", "#0a0a0a"], hint: "Green digital rain" },
  "Amelie (Warm Golden)": { colors: ["#d4a017", "#8b4513", "#2d1f0e"], hint: "Warm amber glow" },
  "Mad Max Fury Road (Bleach Bypass)": { colors: ["#c77b30", "#1c1c0e", "#e8d5a3"], hint: "Bleached desert" },
  "Moonlight (Cool Blue)": { colors: ["#0a1628", "#1a3a5c", "#6baed6"], hint: "Intimate cool blue" },
  "La La Land (Saturated Pop)": { colors: ["#4a0080", "#ff6b9d", "#ffd700"], hint: "Vivid, dreamy" },
  "Sicario (Amber Desert)": { colors: ["#b8860b", "#4a3000", "#daa520"], hint: "Harsh amber heat" },
  "Joker (Muted Grime)": { colors: ["#2f4f2f", "#556b2f", "#8b7355"], hint: "Grimy, muted green" },
  "Her (Pastel Soft)": { colors: ["#ffb6c1", "#ffd4a8", "#ffe4e1"], hint: "Soft warm pastels" },
  "Drive (Neon Noir)": { colors: ["#ff00ff", "#1a0033", "#00ffff"], hint: "Neon-pink noir" },
  "Dune (Golden Sand)": { colors: ["#c19a6b", "#8b6914", "#2c1810"], hint: "Earthy golden" },
  "Midsommar (Overexposed Pastoral)": { colors: ["#fffacd", "#90ee90", "#f0f8ff"], hint: "Bright, overlit" },
  "Parasite (Cool Neutral)": { colors: ["#708090", "#2f4f4f", "#dcdcdc"], hint: "Clean, neutral" },
  "Interstellar (Warm Monochrome)": { colors: ["#8b7355", "#556b2f", "#d2b48c"], hint: "Warm sepia tones" },
  "Euphoria (Neon Purple/Teal)": { colors: ["#8b00ff", "#00ced1", "#ff1493"], hint: "Vivid neon party" },
  "Barry Lyndon (Candlelit)": { colors: ["#b8860b", "#8b4513", "#2c1810"], hint: "Candlelight warmth" },
  "The Revenant (Blue Cold)": { colors: ["#1c3a5c", "#708090", "#b0c4de"], hint: "Frozen blue cold" },
  "Oppenheimer (Black & White IMAX)": { colors: ["#0a0a0a", "#696969", "#d3d3d3"], hint: "IMAX monochrome" },
  "Skyfall (Roger Deakins Shadow)": { colors: ["#1c1c2e", "#b8860b", "#4a4a6a"], hint: "Shadow + amber" },
  "1917 (Desaturated Trench)": { colors: ["#556b2f", "#4a4a3a", "#8b8b7a"], hint: "Desaturated mud" },
  "Barbie (Hot Pink Dreamland)": { colors: ["#ff69b4", "#ff1493", "#ffb6c1"], hint: "Hot pink fantasy" },
};

const CATEGORY_ICONS = {
  'Visual Style & Color': Palette,
  'Shot Composition': Camera,
  'Character & Casting': Users,
  'Lighting & Atmosphere': Sun,
  'VFX & Post': Film,
  'Storyboarding': PenTool,
  'Motion & Video': Move,
  'Production Tools': Wrench,
};

const CATEGORY_COLORS = {
  'Visual Style & Color': { bg: 'rgba(168, 85, 247, 0.15)', border: 'rgba(168, 85, 247, 0.3)', text: '#A855F7' },
  'Shot Composition': { bg: 'rgba(14, 165, 233, 0.15)', border: 'rgba(14, 165, 233, 0.3)', text: '#0EA5E9' },
  'Character & Casting': { bg: 'rgba(236, 72, 153, 0.15)', border: 'rgba(236, 72, 153, 0.3)', text: '#EC4899' },
  'Lighting & Atmosphere': { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.3)', text: '#F59E0B' },
  'VFX & Post': { bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.3)', text: '#10B981' },
  'Storyboarding': { bg: 'rgba(99, 102, 241, 0.15)', border: 'rgba(99, 102, 241, 0.3)', text: '#6366F1' },
  'Motion & Video': { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)', text: '#EF4444' },
  'Production Tools': { bg: 'rgba(156, 163, 175, 0.15)', border: 'rgba(156, 163, 175, 0.3)', text: '#9CA3AF' },
};

export default function RecipeDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const isQuickRun = new URLSearchParams(location.search).get('quickrun') === 'true';
  const quickRunApplied = useRef(false);

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [error, setError] = useState(null);
  const [pollingId, setPollingId] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [showHistory, setShowHistory] = useState(true);
  // AI Photoshop only — tab toggle between Recent Runs and the persistent
  // Gallery of "okayed" iterations. Stored in state (not URL) so navigating
  // away and back keeps the user on the tab they last picked.
  const [aiPhotoshopTab, setAiPhotoshopTab] = useState('runs'); // 'runs' | 'gallery'
  const [galleryRefreshKey, setGalleryRefreshKey] = useState(0);
  const [showMobileInputs, setShowMobileInputs] = useState(false);
  const [progressPct, setProgressPct] = useState(0);

  // Montage parallel runs
  const [activeRuns, setActiveRuns] = useState([]); // [{runId, status, progress, currentNode, startedAt}]
  const activeRunPolls = useRef({}); // runId -> intervalId

  // Ref for scrolling to outputs
  const outputsRef = useRef(null);

  // Input state
  const [textInput, setTextInput] = useState('');
  const [textInput2, setTextInput2] = useState('');
  const [dropdownValue, setDropdownValue] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [image2File, setImage2File] = useState(null);
  const [image2Preview, setImage2Preview] = useState(null);
  const fileRef = useRef(null);
  const file2Ref = useRef(null);

  // Montage Videos state
  const MONTAGE_INIT = { cast: { mode: 'upload', file: null, preview: null, text: '' }, costume: { mode: 'upload', file: null, preview: null, text: '' }, environment: { mode: 'upload', file: null, preview: null, text: '' }, sku: { mode: 'upload', file: null, preview: null, text: '' } };
  const [montageSlots, setMontageSlots] = useState(MONTAGE_INIT);
  const setMontageSlotAndResetProcessed = (updater) => {
    if (typeof updater === 'function') {
      setMontageSlots(prev => { const next = updater(prev); return next; });
    } else {
      setMontageSlots(updater);
    }
    setSlotsProcessed(false);
  };
  const isMontage = slug === 'montage-videos';
  const isUrlToAd = slug === 'url-to-ad';
  const isSeedance25 = slug === 'seedance-25-direct';
  const isSeedanceDirect = slug === 'seedance-direct' || isSeedance25;
  const isHappyHorse = slug === 'happyhorse-direct';
  const isKling = slug === 'kling-i2v';
  const isScriptNormalizer = slug === 'script-normalizer';
  const isAiPhotoshop = slug === 'ai-photoshop';
  const isTVC = slug === 'tvc-20s';

  // Viewport gate for promoting the Montage Timeline Editor to the LEFT
  // (wide) preview panel on desktop. The right panel is ~320-384px which
  // cramps trim/zoom/drag gestures — the left panel is 3× that. On < lg
  // widths, the layout stacks vertically and the timeline stays in the
  // right panel flow (its original home). `matchMedia` listener keeps the
  // render location in sync on viewport changes without full remount spam.
  const [isLgViewport, setIsLgViewport] = useState(
    typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsLgViewport(e.matches);
    // Safari <14 uses addListener; modern browsers use addEventListener
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  // DAW-style "Focus Mode" for the Montage Timeline Editor — when enabled
  // on desktop, the right input/history panel collapses to a 48px rail and
  // the left preview panel expands to fill the full row. Gives the timeline
  // ~95% of the viewport width, matching Premiere/DaVinci edit-only modes.
  // Persisted to localStorage so users don't re-toggle every session.
  const FOCUS_MODE_KEY = `eip_focus_mode_${slug || ''}`;
  const [focusMode, setFocusMode] = useState(() => {
    if (typeof window === 'undefined' || !slug) return false;
    try { return window.localStorage.getItem(FOCUS_MODE_KEY) === '1'; } catch { return false; }
  });
  const toggleFocusMode = useCallback(() => {
    setFocusMode(prev => {
      const next = !prev;
      try { window.localStorage.setItem(FOCUS_MODE_KEY, next ? '1' : '0'); } catch { /* ignore quota */ }
      return next;
    });
  }, [FOCUS_MODE_KEY]);
  // Focus mode only makes sense for timeline-heavy recipes on desktop.
  const focusModeActive = focusMode && isLgViewport && isMontage;

  // URL-to-Ad video state (displayed in left panel)
  const [adVideoGenerating, setAdVideoGenerating] = useState(false);
  const [adVideoUrl, setAdVideoUrl] = useState(null);
  const [adRestoredData, setAdRestoredData] = useState(null);
  const [adResetKey, setAdResetKey] = useState(0);

  // Seedance Direct video state (displayed in left panel)
  const [sdVideoGenerating, setSdVideoGenerating] = useState(false);
  const [sdVideoUrl, setSdVideoUrl] = useState(null);
  const [sdRestoredData, setSdRestoredData] = useState(null);
  const [sdResetKey, setSdResetKey] = useState(0);
  const [sdKeyframeUrl, setSdKeyframeUrl] = useState(null);
  const [sdLastFrameUrl, setSdLastFrameUrl] = useState(null);
  const [sdPrompt, setSdPrompt] = useState('');

  // HappyHorse video state (reuses same left panel pattern as Seedance Direct)
  const [hhVideoGenerating, setHhVideoGenerating] = useState(false);
  const [hhVideoUrl, setHhVideoUrl] = useState(null);
  const [hhRestoredData, setHhRestoredData] = useState(null);
  const [hhResetKey, setHhResetKey] = useState(0);
  const [hhKeyframeUrl, setHhKeyframeUrl] = useState(null);
  const [hhPrompt, setHhPrompt] = useState('');

  // Kling video state
  const [klVideoGenerating, setKlVideoGenerating] = useState(false);
  const [klVideoUrl, setKlVideoUrl] = useState(null);
  const [klRestoredData, setKlRestoredData] = useState(null);
  const [klResetKey, setKlResetKey] = useState(0);
  const [klKeyframeUrl, setKlKeyframeUrl] = useState(null);
  const [klLastFrameUrl, setKlLastFrameUrl] = useState(null);
  const [klPrompt, setKlPrompt] = useState('');

  const resetAdmaker = () => {
    setAdVideoUrl(null);
    setAdVideoGenerating(false);
    setAdRestoredData(null);
    setRunResult(null);
    setMontageSlots({ cast: {}, costume: {}, environment: {}, sku: {} });
    setAdResetKey(k => k + 1); // force remount of URLtoAdInputs
  };
  const resetSeedanceDirect = () => {
    setSdVideoUrl(null);
    setSdVideoGenerating(false);
    setSdRestoredData(null);
    setSdKeyframeUrl(null);
    setSdLastFrameUrl(null);
    setRunResult(null);
    setSdResetKey(k => k + 1);
  };
  const [montageVideoReady, setMontageVideoReady] = useState(false);
  const [enhancePrompt, setEnhancePrompt] = useState(false);
  const [promptMode, setPromptMode] = useState('auto');
  const [photoshopShotId, setPhotoshopShotId] = useState(null);
  const [photoshopEditUrl, setPhotoshopEditUrl] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [slotsProcessed, setSlotsProcessed] = useState(false);
  const [processing, setProcessing] = useState(false); // 'auto' = style engine, 'manual' = user types
  const [iteratingSlot, setIteratingSlot] = useState(null); // { key: 'cast'|'costume'|... }
  const [iterateFeedback, setIterateFeedback] = useState('');

  // Lightbox
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // Pinch-to-zoom state
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomPos, setZoomPos] = useState({ x: 0, y: 0 });
  const lastTouchRef = useRef({ dist: 0, ts: 0, x: 0, y: 0 });
  const zoomImgRef = useRef(null);

  // Favorites
  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    // Clear stale state first, before loading new recipe
    setError(null);
    setRunResult(null);
    setImageFile(null);
    setImagePreview(null);
    setImage2File(null);
    setImage2Preview(null);
    setTextInput('');
    setTextInput2('');
    setDropdownValue('');
    setProgressPct(0);
    setCurrentNode(null);
    setShowMobileInputs(false);
    setMontageSlots({ cast: { mode: 'upload', file: null, preview: null, text: '' }, costume: { mode: 'upload', file: null, preview: null, text: '' }, environment: { mode: 'upload', file: null, preview: null, text: '' }, sku: { mode: 'upload', file: null, preview: null, text: '' } });
    setMontageVideoReady(false);
    setAdVideoUrl(null);
    setAdVideoGenerating(false);
    setAdRestoredData(null);
    // Then load new data
    loadRecipe();
    loadRunHistory();
    loadFavoriteStatus();
  }, [slug]);

  // Set browser tab title
  useEffect(() => {
    if (recipe?.name) {
      document.title = `${recipe.name} — Eipicenter`;
    }
    return () => { document.title = 'Eipicenter'; };
  }, [recipe?.name]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingId) clearInterval(pollingId);
      Object.values(activeRunPolls.current).forEach(id => clearInterval(id));
    };
  }, [pollingId]);

  // On mount: re-discover any processing montage runs
  useEffect(() => {
    if (!isMontage || !token || !slug) return;
    const rediscoverRuns = async () => {
      try {
        const { data } = await axios.get(`${API}/api/recipes/${slug}/runs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const runs = data?.runs || data || [];
        const processing = runs.filter(r => r.status === 'processing');
        if (processing.length > 0) {
          const newActiveRuns = processing.map(r => ({
            runId: r.run_id,
            status: 'processing',
            progress: r.progress_pct || 0,
            currentNode: r.current_node || 'Processing...',
            startedAt: r.started_at,
            outputs: null,
            error: null,
          }));
          setActiveRuns(prev => {
            const existingIds = new Set(prev.map(r => r.runId));
            const toAdd = newActiveRuns.filter(r => !existingIds.has(r.runId));
            return [...prev, ...toAdd];
          });
          processing.forEach(r => {
            if (!activeRunPolls.current[r.run_id]) {
              startRunPolling(r.run_id);
            }
          });
        }
      } catch { /* silent */ }
    };
    rediscoverRuns();
  }, [isMontage, token, slug]);

  // Start polling for a montage parallel run
  const startRunPolling = (runId) => {
    const interval = setInterval(async () => {
      try {
        const { data: status } = await axios.get(`${API}/api/recipes/${slug}/runs/${runId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (status.status === 'completed') {
          clearInterval(interval);
          delete activeRunPolls.current[runId];
          setActiveRuns(prev => prev.map(r => r.runId === runId ? { ...r, status: 'completed', progress: 100, currentNode: 'Done', outputs: status.outputs } : r));
          loadRunHistory();
          toast.success('Video ready!');
        } else if (status.status === 'failed') {
          clearInterval(interval);
          delete activeRunPolls.current[runId];
          setActiveRuns(prev => prev.map(r => r.runId === runId ? { ...r, status: 'failed', error: status.error || 'Failed' } : r));
          toast.error('Generation failed');
        } else {
          setActiveRuns(prev => prev.map(r => r.runId === runId ? { ...r, currentNode: status.current_node || r.currentNode, progress: status.progress_pct ?? r.progress } : r));
        }
      } catch { /* keep polling */ }
    }, 3000);
    activeRunPolls.current[runId] = interval;
  };

  const loadRecipe = async () => {
    // Handle numeric slugs gracefully
    if (/^\d+$/.test(slug)) {
      setError('Recipe not found. Recipes use named URLs (e.g., /recipes/cinematic-color-grade).');
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/recipes/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipe(data);
      // Set default dropdown value
      const ddInput = (data.inputs || []).find(i => i.type === 'dropdown');
      if (ddInput?.options?.length) setDropdownValue(ddInput.options[0]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Recipe not found');
    } finally {
      setLoading(false);
    }
  };

  const loadRunHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/recipes/${slug}/runs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRunHistory(data.runs || []);
    } catch (err) { /* ignore */ }
  };

  // Quick Run: pre-fill inputs from last completed run
  useEffect(() => {
    if (!isQuickRun || quickRunApplied.current || !recipe || runHistory.length === 0) return;
    const lastCompleted = runHistory.find(r => r.status === 'completed');
    if (!lastCompleted?.inputs) return;
    quickRunApplied.current = true;
    const { text_input, dropdown_value, text_input_2 } = lastCompleted.inputs;
    if (text_input) setTextInput(text_input);
    if (text_input_2) setTextInput2(text_input_2);
    if (dropdown_value) setDropdownValue(dropdown_value);
    // Check if recipe needs image input
    const needsImage = (recipe.inputs || []).some(i => i.type === 'image');
    if (needsImage) {
      toast.info('Last settings restored — upload an image and hit Generate');
    } else {
      toast.success('Last settings restored — hit Generate to run again');
    }
    // Clean up URL without re-render
    window.history.replaceState({}, '', `/recipes/${slug}`);
  }, [isQuickRun, recipe, runHistory]); // eslint-disable-line

  // State for video duration badge
  const [videoDuration, setVideoDuration] = useState(null);
  const [video2Duration, setVideo2Duration] = useState(null);

  const loadFavoriteStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/recipes/user/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorited((data.favorites || []).includes(slug));
    } catch (err) { /* non-critical */ }
  };

  const toggleFavorite = async () => {
    try {
      const { data } = await axios.post(`${API}/api/recipes/${slug}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorited(data.favorited);
      toast.success(data.favorited ? 'Added to favorites' : 'Removed from favorites');
    } catch (err) {
      toast.error('Failed to update favorite');
    }
  };

  const handleImageSelect = (e, which) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Issue 2: File size check (50MB max)
    const MAX_FILE_SIZE = 50 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large (${(file.size/1024/1024).toFixed(0)}MB). Maximum 50MB.`);
      return;
    }
    
    const isVideo = file.type.startsWith('video/');
    if (isVideo) {
      const url = URL.createObjectURL(file);
      
      // Issue 1: Check video duration before accepting
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const maxDuration = recipe?.max_video_duration || 8;
        if (which === 1) setVideoDuration(video.duration);
        else setVideo2Duration(video.duration);
        
        if (video.duration > maxDuration) {
          toast.error(`Video too long (${video.duration.toFixed(1)}s). Maximum ${maxDuration} seconds.`);
          URL.revokeObjectURL(url);
          return;
        }
        if (which === 1) { setImageFile(file); setImagePreview(url); }
        else { setImage2File(file); setImage2Preview(url); }
      };
      video.src = url;
    } else {
      if (which === 1) setVideoDuration(null);
      else setVideo2Duration(null);
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (which === 1) { setImageFile(file); setImagePreview(ev.target.result); }
        else { setImage2File(file); setImage2Preview(ev.target.result); }
      };
      reader.readAsDataURL(file);
    }
  };

  const isVideoFile = (file) => file && file.type && file.type.startsWith('video/');

  const applyPreset = (preset) => {
    const inputs = preset.inputs || {};
    if (inputs.text_input) setTextInput(inputs.text_input);
    if (inputs.dropdown_value) setDropdownValue(inputs.dropdown_value);
    if (inputs.text_input_2) setTextInput2(inputs.text_input_2);
    toast.success(`Preset "${preset.name}" applied`);
  };

  // Issue 3: Conditional visibility for inputs with show_when
  const shouldShowInput = (input) => {
    if (!input.show_when) return true;
    const [field, val] = Object.entries(input.show_when)[0];
    if (field === 'dropdown_value') return dropdownValue === val;
    if (field === 'text_input') return textInput === val;
    return true;
  };

  // Issue 7: Compute whether all required inputs are filled
  const canGenerate = useMemo(() => {
    if (!recipe) return false;
    // Montage: style dropdown value is in textInput, always has a default
    if (isMontage) return true;
    if (isUrlToAd) return false; // URL-to-Ad has its own Generate flow inside the component
    if (isSeedanceDirect || isHappyHorse || isKling) return false; // Seedance Direct has its own Generate flow
    for (const input of recipe.inputs || []) {
      if (!input.required) continue;
      if (input.type === 'image' && input.name === 'image' && !imageFile) return false;
      if (input.type === 'image' && input.name === 'image2' && !image2File) return false;
      if (input.type === 'text' && input.name === 'text_input' && !textInput?.trim()) return false;
      if (input.type === 'text' && input.name === 'text_input_2' && !textInput2?.trim()) return false;
    }
    return true;
  }, [recipe, imageFile, image2File, textInput, textInput2, isMontage, montageSlots]);


  const processSlots = async () => {
    setProcessing(true);
    const { cast, costume, environment, sku } = montageSlots;
    const ar = dropdownValue?.includes('9:16') ? '9:16' : dropdownValue?.includes('1:1') ? '1:1' : '16:9';
    const headers = { Authorization: `Bearer ${token}` };

    const fetchPreview = async (url) => {
      if (!url) return null;
      try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        return await new Promise(r => { const reader = new FileReader(); reader.onload = () => r(reader.result); reader.readAsDataURL(blob); });
      } catch { return url; }
    };

    // Poll a montage task until done/failed (3s interval, 3min max, 404-retry)
    const pollTask = async (taskId) => {
      const maxPolls = 100;
      let consecutive404 = 0;
      for (let i = 0; i < maxPolls; i++) {
        await new Promise(r => setTimeout(r, 3000));
        let data;
        try {
          const resp = await axios.get(`${API}/api/recipes/montage-task/${taskId}`, {
            headers, timeout: 10000,
          });
          data = resp.data;
          consecutive404 = 0;
        } catch (err) {
          if (err.response?.status === 404) {
            consecutive404 += 1;
            if (consecutive404 >= 3) throw new Error('Task not found — backend may have restarted. Please retry.');
            continue;
          }
          continue;
        }
        if (data.status === 'done') return data;
        if (data.status === 'failed') throw new Error(data.error || 'Processing failed');
      }
      throw new Error('Processing timed out — please try again');
    };

    const processOne = async (slotKey, slotData) => {
      if (!slotData?.file && !slotData?.processedUrl) return null;
      
      // SKU is passthrough — no AI processing needed, just use the image as-is
      if (slotKey === 'sku') {
        if (slotData.processedUrl) return { preview: slotData.preview, processedUrl: slotData.processedUrl, file: null };
        if (slotData.file) {
          // Upload to S3 via process endpoint
          try {
            const fd = new FormData();
            fd.append('sku_image', slotData.file);
            fd.append('aspect_ratio', ar);
            const { data } = await axios.post(`${API}/api/recipes/process-montage-slots`, fd, {
              headers: { ...headers, 'Content-Type': 'multipart/form-data' }, timeout: 60000,
            });
            if (!data.task_id) throw new Error('No task_id');
            const result = await pollTask(data.task_id);
            const url = result.results?.sku;
            if (url) {
              const preview = await fetchPreview(url);
              return { preview, processedUrl: url, file: null };
            }
          } catch (err) {
            // SKU fallback: use local blob URL
            const blobUrl = URL.createObjectURL(slotData.file);
            return { preview: blobUrl, processedUrl: blobUrl, file: slotData.file };
          }
        }
        return null;
      }

      try {
        const fd = new FormData();
        if (slotData.file) fd.append(`${slotKey}_image`, slotData.file);
        else if (slotData.processedUrl) fd.append(`${slotKey}_url`, slotData.processedUrl);
        fd.append('aspect_ratio', ar);
        // Submit — returns task_id instantly
        const { data } = await axios.post(`${API}/api/recipes/process-montage-slots`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }, timeout: 60000,
        });
        if (!data.task_id) throw new Error('No task_id');
        // Poll until done
        const result = await pollTask(data.task_id);
        const url = result.results?.[slotKey];
        if (url) {
          const preview = await fetchPreview(url);
          return { preview, processedUrl: url, file: null };
        }
        const slotErr = result.results?.[`${slotKey}_error`];
        if (slotErr) toast.warning(`${slotKey}: ${slotErr.slice(0, 80)}`);
        return null;
      } catch (err) {
        toast.warning(`${slotKey} failed: ${err.message || 'Unknown error'}`);
        return null;
      }
    };

    // Process each slot sequentially with polling
    const slots = [
      { key: 'cast', data: cast },
      { key: 'costume', data: costume },
      { key: 'environment', data: environment },
      { key: 'sku', data: sku },
    ];

    let processedCount = 0;
    for (const { key, data: slotData } of slots) {
      if (!slotData?.file && !slotData?.processedUrl) continue;
      
      // If slot already has a processedUrl and no new file, it's already processed — skip re-processing
      if (!slotData.file && slotData.processedUrl) {
        processedCount++;
        // Mark as processed in slot state so the badge shows up
        setMontageSlots(prev => ({ ...prev, [key]: { ...prev[key], processStatus: 'processed', processError: null } }));
        continue;
      }
      
      toast.info(`Processing ${key}...`);
      // Mark as processing so a spinner/badge can render
      setMontageSlots(prev => ({ ...prev, [key]: { ...prev[key], processStatus: 'processing', processError: null } }));
      try {
        const result = await processOne(key, slotData);
        if (result) {
          setMontageSlots(prev => ({ ...prev, [key]: { ...prev[key], ...result, processStatus: 'processed', processError: null } }));
          processedCount++;
        } else {
          setMontageSlots(prev => ({ ...prev, [key]: { ...prev[key], processStatus: 'failed', processError: `${key} processing returned no result` } }));
        }
      } catch (err) {
        const msg = err?.message || 'Unknown error';
        setMontageSlots(prev => ({ ...prev, [key]: { ...prev[key], processStatus: 'failed', processError: msg } }));
        toast.warning(`${key} failed: ${msg.slice(0, 120)}`);
      }
    }

    if (processedCount > 0) {
      setSlotsProcessed(true);
      toast.success(`${processedCount} slots processed!`);
    } else {
      toast.error('No slots could be processed');
    }
    setProcessing(false);
  };

  // Iterate a single processed slot with text feedback
  const iterateProcessedSlot = async (slotKey) => {
    if (!iterateFeedback.trim() || !montageSlots[slotKey]?.processedUrl) return;
    setProcessing(true);
    toast.info(`Re-processing ${slotKey}...`);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const fd = new FormData();
      fd.append(`${slotKey}_url`, montageSlots[slotKey].processedUrl);
      fd.append('feedback', iterateFeedback.trim());
      fd.append('aspect_ratio', montageSlots.aspectRatio || '16:9');
      const { data } = await axios.post(`${API}/api/recipes/iterate-montage-slot`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }, timeout: 60000,
      });
      // If the endpoint doesn't exist yet, fall back to process-montage-slots
      if (!data.task_id) throw new Error('No task_id');
      // Poll
      const maxPolls = 60;
      let result = null;
      for (let i = 0; i < maxPolls; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: taskData } = await axios.get(`${API}/api/recipes/montage-task/${data.task_id}`, { headers, timeout: 10000 });
        if (taskData.status === 'done') { result = taskData; break; }
        if (taskData.status === 'failed') throw new Error(taskData.error || 'Iteration failed');
      }
      if (!result) throw new Error('Timed out');
      const url = result.results?.[slotKey];
      if (url) {
        let preview = url;
        try {
          const resp = await axios.get(url, { responseType: 'blob', timeout: 10000 });
          preview = URL.createObjectURL(resp.data);
        } catch {}
        setMontageSlots(prev => ({ ...prev, [slotKey]: { ...prev[slotKey], processedUrl: url, preview } }));
        toast.success(`${slotKey} updated!`);
      }
    } catch (err) {
      toast.error(`${slotKey} iteration failed: ${err.message || ''}`);
    }
    setIteratingSlot(null);
    setIterateFeedback('');
    setProcessing(false);
  };

  /**
   * Reprocess a single slot (used by the Re-process icon on failed slot tiles).
   * Independent of `processSlots` so users can retry just one slot without
   * re-processing the entire quartet.
   */
  const reprocessSingleSlot = async (slotKey) => {
    const slot = montageSlots[slotKey];
    // Need either an uploaded file OR a previously-processed URL to re-run
    if (!slot?.file && !slot?.processedUrl) {
      toast.error(`${slotKey}: no source image to reprocess`);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    setMontageSlots(prev => ({ ...prev, [slotKey]: { ...prev[slotKey], processStatus: 'processing', processError: null } }));
    toast.info(`Reprocessing ${slotKey}...`);
    const ar = montageSlots.aspectRatio || '16:9';

    // Local poller (mirrors pollTask with 3-consecutive-404 tolerance)
    const poll = async (taskId) => {
      let consecutive404 = 0;
      for (let i = 0; i < 100; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const { data } = await axios.get(`${API}/api/recipes/montage-task/${taskId}`, { headers, timeout: 10000 });
          consecutive404 = 0;
          if (data.status === 'done') return data;
          if (data.status === 'failed') throw new Error(data.error || 'Processing failed');
        } catch (err) {
          if (err.response?.status === 404) {
            consecutive404 += 1;
            if (consecutive404 >= 3) throw new Error('Task not found — backend may have restarted. Please retry.');
          }
        }
      }
      throw new Error('Reprocess timed out');
    };

    try {
      const fd = new FormData();
      if (slot.file) fd.append(`${slotKey}_image`, slot.file);
      else if (slot.processedUrl) fd.append(`${slotKey}_url`, slot.processedUrl);
      fd.append('aspect_ratio', ar);
      const { data } = await axios.post(`${API}/api/recipes/process-montage-slots`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }, timeout: 60000,
      });
      if (!data.task_id) throw new Error('No task_id returned');
      const result = await poll(data.task_id);
      const url = result.results?.[slotKey];
      const slotErr = result.results?.[`${slotKey}_error`];
      if (url) {
        let preview = url;
        try {
          const resp = await axios.get(url, { responseType: 'blob', timeout: 10000 });
          preview = URL.createObjectURL(resp.data);
        } catch {}
        setMontageSlots(prev => ({ ...prev, [slotKey]: { ...prev[slotKey], processedUrl: url, preview, file: null, processStatus: 'processed', processError: null } }));
        toast.success(`${slotKey} reprocessed`);
      } else {
        throw new Error(slotErr || 'Reprocessing returned no result');
      }
    } catch (err) {
      const msg = err?.message || 'Unknown error';
      setMontageSlots(prev => ({ ...prev, [slotKey]: { ...prev[slotKey], processStatus: 'failed', processError: msg } }));
      toast.error(`${slotKey} reprocess failed: ${msg.slice(0, 120)}`);
    }
  };


  const runRecipe = async () => {
    // Montage uses style engine, no free text needed
    if (!isMontage && !textInput?.trim()) {
      toast.error('Scene prompt is required');
      return;
    }
    // Non-montage validation
    if (!isMontage) {
      const requiredInputs = (recipe?.inputs || []).filter(i => i.required);
      for (const input of requiredInputs) {
        if (input.type === 'image' && input.name === 'image' && !imageFile) {
          toast.error(`${input.label || 'Image'} is required`);
          return;
        }
      }
    }
    
    // Clear any stale polling from a previous stuck run before starting a new one
    if (pollingId) {
      clearInterval(pollingId);
      setPollingId(null);
    }

    if (!isMontage) setRunning(true);
    setRunResult(null);
    setError(null);
    setProgressPct(0);
    setCurrentNode(null);

    try {
      const formData = new FormData();

      if (isMontage) {
        // Montage: send processed URLs (already refined + S-Log3) or raw inputs
        const { cast, costume, environment, sku } = montageSlots;
        // For each slot: if processed, send the final URL as text; if raw, send file/text
        if (cast.processedUrl) formData.append('cast_text', `PROCESSED_URL:${cast.processedUrl}`);
        else if (cast.file) formData.append('image', cast.file);
        else if (cast.text?.trim()) formData.append('cast_text', cast.text.trim());

        if (costume.processedUrl) formData.append('costume_text', `PROCESSED_URL:${costume.processedUrl}`);
        else if (costume.file) formData.append('image2', costume.file);
        else if (costume.text?.trim()) formData.append('costume_text', costume.text.trim());

        if (environment.processedUrl) formData.append('environment_text', `PROCESSED_URL:${environment.processedUrl}`);
        else if (environment.file) formData.append('image3', environment.file);
        else if (environment.text?.trim()) formData.append('environment_text', environment.text.trim());

        if (sku?.processedUrl) formData.append('sku_text', `PROCESSED_URL:${sku.processedUrl}`);
        else if (sku?.file) formData.append('image4', sku.file);
        else if (sku?.text?.trim()) formData.append('sku_text', sku.text.trim());
      } else {
        if (imageFile) formData.append('image', imageFile);
        if (image2File) formData.append('image2', image2File);
        if (textInput2) formData.append('text_input_2', textInput2);
      }
      if (textInput) formData.append('text_input', textInput);
      if (dropdownValue) formData.append('dropdown_value', dropdownValue);
      if (isMontage) {
        // Resolve style with default
        const resolvedStyle = textInput?.trim() || 'Streetwear Fashion Reel';
        formData.set('text_input', resolvedStyle);

        if (promptMode === 'manual' && montageSlots.manualPrompt?.trim()) {
          formData.append('enhance_prompt', '0');
          formData.set('text_input', montageSlots.manualPrompt.trim());
        } else {
          formData.append('enhance_prompt', '1');
        }
        if (textInput2) formData.append('text_input_2', textInput2);
        if (montageSlots.mood) formData.append('mood_music', montageSlots.mood);
        if (montageSlots.keywords) formData.append('keywords', montageSlots.keywords);
      }

      const { data } = await axios.post(`${API}/api/recipes/${slug}/run`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      const runId = data.run_id;
      toast.success('Recipe started! Processing...');

      if (isMontage) {
        // Parallel runs: add to active runs array, start independent polling
        setActiveRuns(prev => [...prev, {
          runId,
          status: 'processing',
          progress: 0,
          currentNode: 'Starting...',
          startedAt: new Date().toISOString(),
          outputs: null,
          error: null,
        }]);
        startRunPolling(runId);

        // Scroll to output view on mobile
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 300);
      } else {
        // Non-montage: single run behavior (original)

        // Poll for results (single run) with safety guards
        const pollStartedAt = Date.now();
        const MAX_POLL_MS = (isTVC ? 25 : 15) * 60 * 1000; // 25 min for TVC, 15 for others
        // TVC fans out 5 parallel Seedance calls → backend queues can stall
        // individual shots without failing the overall run. Tolerate more
        // transient poll errors for TVC so the UI doesn't falsely toast
        // "Lost connection" mid-pipeline.
        const MAX_CONSECUTIVE_FAILURES = isTVC ? 18 : 5;
        let consecutiveFailures = 0;

        const interval = setInterval(async () => {
        try {
          const { data: status } = await axios.get(`${API}/api/recipes/${slug}/runs/${runId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          consecutiveFailures = 0; // reset on any successful poll
          if (status.status === 'completed') {
            clearInterval(interval);
            setPollingId(null);
            setCurrentNode(null);
            setProgressPct(100);
            setRunResult(status);
            setRunning(false);
            loadRunHistory();
            toast.success('Recipe complete!');

            // MONTAGE: bridge the universes — push the run's processed
            // reference images back into the slot tile state so users can
            // actually see the processed mugshot / flat-lay / establishing
            // shot / SKU on the right panel. Previously the tiles kept
            // showing the raw uploads while the output gallery showed the
            // processed versions (confusing disconnect).
            if (slug === 'montage-videos' && Array.isArray(status.outputs)) {
              const labelMap = {
                'Cast Reference': 'cast',
                'Costume Reference': 'costume',
                'Environment Reference': 'environment',
                'SKU Reference': 'sku',
              };
              const updates = {};
              for (const o of status.outputs) {
                if (o.type !== 'image' || !o.url) continue;
                const slotKey = labelMap[o.label];
                if (!slotKey) continue;
                const absUrl = o.url.startsWith('http') ? o.url : `${API}${o.url}`;
                updates[slotKey] = { processedUrl: absUrl, preview: absUrl, file: null, processStatus: 'processed', processError: null };
              }
              if (Object.keys(updates).length) {
                setMontageSlots(prev => {
                  const next = { ...prev };
                  for (const [k, v] of Object.entries(updates)) {
                    next[k] = { ...(prev[k] || {}), ...v };
                  }
                  return next;
                });
                setSlotsProcessed(true);
              }
            }

            // Scroll to outputs on completion (especially important on mobile)
            setTimeout(() => {
              const previewPanel = document.querySelector('[data-testid="recipe-preview-panel"]');
              if (previewPanel) previewPanel.scrollTo({ top: 0, behavior: 'smooth' });
              const parent = document.querySelector('[data-testid="main-content-wrapper"]');
              if (parent) parent.scrollTo({ top: 0, behavior: 'smooth' });
            }, 200);
          } else if (status.status === 'failed') {
            clearInterval(interval);
            setPollingId(null);
            setCurrentNode(null);
            setError(status.error || 'Recipe execution failed');
            setRunning(false);
            toast.error('Recipe failed');
          } else if (status.current_node) {
            setCurrentNode(status.current_node);
            if (status.progress_pct !== undefined) {
              setProgressPct(status.progress_pct);
            }
            // TVC pipeline view needs live mid-run state so the 5-stage
            // column can render per-shot progress as it happens. Other
            // recipes keep the "only update on completion" behaviour to
            // avoid re-renders mid-run.
            if (isTVC && status.tvc_state) {
              setRunResult(status);
            }
          }
          // Hard timeout safety net
          if (Date.now() - pollStartedAt > MAX_POLL_MS) {
            clearInterval(interval);
            setPollingId(null);
            setCurrentNode(null);
            setRunning(false);
            setError('Recipe timed out — please try again');
            toast.error('Recipe timed out');
          }
        } catch (err) {
          consecutiveFailures += 1;
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            clearInterval(interval);
            setPollingId(null);
            setCurrentNode(null);
            setRunning(false);
            const msg = err.response?.status === 404
              ? 'Run not found — backend may have restarted. Please try again.'
              : 'Lost connection to server — please try again.';
            setError(msg);
            toast.error(msg);
          }
        }
      }, 3000);
      setPollingId(interval);
      } // end non-montage else

    } catch (err) {
      // Roll back ALL optimistic UI state on POST failure
      if (!isMontage) {
        setRunning(false);
        setProgressPct(0);
        setCurrentNode(null);
      }
      if (pollingId) {
        clearInterval(pollingId);
        setPollingId(null);
      }
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      const msg = status === 404
        ? 'Recipe endpoint unavailable (backend restarting) — please try again in a few seconds.'
        : (detail || 'Failed to start recipe');
      setError(msg);
      toast.error(msg);
    }
  };

  const loadPreviousRun = async (run) => {
    setRunResult(run);
    // Restore prompt from the loaded run
    if (run?.inputs?.text_input) setTextInput(run.inputs.text_input);
    // TVC pipeline view needs the full run doc (which includes tvc_state);
    // the history list endpoint may strip that field to keep the list small,
    // so fetch the canonical run by id and hydrate runResult with it.
    if (isTVC && run?.run_id) {
      try {
        const { data: full } = await axios.get(
          `${API}/api/recipes/${slug}/runs/${run.run_id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (full) setRunResult(full);
      } catch (e) { /* keep the shallow copy */ }
    }
    // For URL-to-Ad: extract video URL and restore all inputs
    if (isUrlToAd && run?.outputs?.length > 0) {
      const videoOut = run.outputs.find(o => o.type === 'video' && o.url);
      if (videoOut) {
        setAdVideoUrl(videoOut.url);
        setAdVideoGenerating(false);
      }
    }
    if (isUrlToAd && run?.inputs) {
      setAdRestoredData({
        product: run.inputs.product,
        script: run.inputs.script,
        shots: run.inputs.shots,
        tonality: run.inputs.tonality,
        aspectRatio: run.inputs.aspectRatio,
        slots: run.inputs.slots,
        idea: run.inputs.idea,
        targetAudience: run.inputs.targetAudience,
        ctaText: run.inputs.ctaText,
        sourceUrl: run.inputs.sourceUrl,
      });
      // Restore CCC slots
      if (run.inputs.slots) {
        setMontageSlots(prev => ({
          ...prev,
          cast: { ...(prev.cast || {}), ...(run.inputs.slots.cast || {}) },
          costume: { ...(prev.costume || {}), ...(run.inputs.slots.costume || {}) },
          environment: { ...(prev.environment || {}), ...(run.inputs.slots.environment || {}) },
          sku: { ...(prev.sku || {}), ...(run.inputs.slots.sku || {}) },
        }));
      }
    }
    // For Seedance Direct: extract video URL and restore inputs
    if (isSeedanceDirect && run?.outputs?.length > 0) {
      const videoOut = run.outputs.find(o => o.type === 'video' && o.url);
      if (videoOut) {
        setSdVideoUrl(videoOut.url);
        setSdVideoGenerating(false);
      }
    }
    if (isSeedanceDirect && run?.inputs) {
      setSdRestoredData({
        prompt: run.inputs.prompt,
        aspect_ratio: run.inputs.aspect_ratio,
        duration: run.inputs.duration,
        resolution: run.inputs.resolution,
        image_url: run.inputs.image_url,
        image_urls: run.inputs.image_urls,
        video_url: run.outputs?.find(o => o.type === 'video')?.url,
      });
      const firstRef = run.inputs.image_url || run.inputs.image_urls?.[0];
      if (firstRef) setSdKeyframeUrl(firstRef);
    }
    // If run is still processing, re-enable running state
    if (run?.status === 'processing') {
      setRunning(true);
      setCurrentNode(run.current_node || 'Processing...');
      setProgressPct(run.progress_pct || 30);
    }
    // Check for photoshop workbench
    const wbOutput = run?.outputs?.find(o => o.label === 'workbench_shot_id');
    if (wbOutput?.content) setPhotoshopShotId(wbOutput.content);
  };

  // Auto-detect photoshop workbench from current run result
  useEffect(() => {
    if (!runResult?.outputs) return;
    const wbOutput = runResult.outputs.find(o => o.label === 'workbench_shot_id');
    if (wbOutput?.content && !photoshopShotId) {
      setPhotoshopShotId(wbOutput.content);
    }
  }, [runResult]);

  const resolveOutputUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API}${url}`;
  };

  // Get all image URLs from current run for lightbox navigation
  const imageOutputUrls = useMemo(() => {
    if (!runResult?.outputs) return [];
    return runResult.outputs
      .filter(o => o.type === 'image' && o.url)
      .map(o => resolveOutputUrl(o.url));
  }, [runResult]);

  const openLightbox = (url) => {
    const idx = imageOutputUrls.indexOf(url);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxUrl(url);
    setZoomScale(1);
    setZoomPos({ x: 0, y: 0 });
  };
  
  const lightboxPrev = () => {
    if (imageOutputUrls.length <= 1) return;
    const idx = (lightboxIndex - 1 + imageOutputUrls.length) % imageOutputUrls.length;
    setLightboxIndex(idx);
    setLightboxUrl(imageOutputUrls[idx]);
    setZoomScale(1);
    setZoomPos({ x: 0, y: 0 });
  };
  
  const lightboxNext = () => {
    if (imageOutputUrls.length <= 1) return;
    const idx = (lightboxIndex + 1) % imageOutputUrls.length;
    setLightboxIndex(idx);
    setLightboxUrl(imageOutputUrls[idx]);
    setZoomScale(1);
    setZoomPos({ x: 0, y: 0 });
  };

  // Pinch-to-zoom touch handlers for lightbox
  const getTouchDist = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };
  const handleLightboxTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      lastTouchRef.current.dist = getTouchDist(e.touches);
    } else if (e.touches.length === 1) {
      const now = Date.now();
      const { ts, x, y } = lastTouchRef.current;
      const dx = Math.abs(e.touches[0].clientX - x);
      const dy = Math.abs(e.touches[0].clientY - y);
      if (now - ts < 300 && dx < 30 && dy < 30) {
        e.preventDefault();
        if (zoomScale > 1) { setZoomScale(1); setZoomPos({ x: 0, y: 0 }); }
        else { setZoomScale(2.5); }
      }
      lastTouchRef.current.ts = now;
      lastTouchRef.current.x = e.touches[0].clientX;
      lastTouchRef.current.y = e.touches[0].clientY;
    }
  };
  const handleLightboxTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const newDist = getTouchDist(e.touches);
      const delta = newDist / lastTouchRef.current.dist;
      setZoomScale(s => Math.max(1, Math.min(5, s * delta)));
      lastTouchRef.current.dist = newDist;
    } else if (e.touches.length === 1 && zoomScale > 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - lastTouchRef.current.x;
      const dy = e.touches[0].clientY - lastTouchRef.current.y;
      setZoomPos(p => ({ x: p.x + dx, y: p.y + dy }));
      lastTouchRef.current.x = e.touches[0].clientX;
      lastTouchRef.current.y = e.touches[0].clientY;
    }
  };
  const handleLightboxTouchEnd = () => {
    if (zoomScale <= 1) setZoomPos({ x: 0, y: 0 });
  };

  if (loading) {
    return (
      <div className="h-screen bg-[#0B0F19] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0EA5E9]" />
      </div>
    );
  }

  if (error && !recipe) {
    return (
      <div className="h-screen bg-[#0B0F19] flex flex-col items-center justify-center text-white">
        <XCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-sm text-white/60">{error}</p>
        <button onClick={() => navigate('/recipes')} className="mt-4 text-xs text-[#0EA5E9] hover:underline">Back to Recipes</button>
      </div>
    );
  }

  const colors = CATEGORY_COLORS[recipe?.category] || CATEGORY_COLORS['Production Tools'];
  const CatIcon = CATEGORY_ICONS[recipe?.category] || Wrench;

  return (
    <div className="h-screen bg-[#0B0F19] text-white flex flex-col overflow-hidden" data-testid="recipe-detail-page">
      {/* Header with EiPi branding */}
      <header className="h-12 px-3 flex items-center justify-between border-b border-white/[0.06] flex-shrink-0"
        style={{ background: 'rgba(11, 15, 25, 0.9)', backdropFilter: 'blur(20px)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-white/5 rounded-lg lg:hidden" data-testid="mobile-menu-btn">
            <ArrowLeft className="w-4 h-4 text-white/50" />
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex-shrink-0 hover:opacity-100 transition-opacity rounded-md focus:outline-none focus:ring-1 focus:ring-white/20"
            title="Back to home"
            data-testid="recipe-detail-logo-home"
          >
            <img src="/assets/eipi-logo.png" alt="Eipicenter — back to home" className="h-4 w-auto brightness-0 invert opacity-70 hover:opacity-100 transition-opacity" />
          </button>
          <div className="w-px h-4 bg-white/[0.08] mx-1 hidden sm:block" />
          <div className="flex items-center gap-1.5 text-[11px] text-white/30 min-w-0 overflow-hidden">
            <span className="hover:text-white/50 cursor-pointer flex-shrink-0" onClick={() => navigate('/recipes')}>Recipes</span>
            <ChevronRight className="w-2.5 h-2.5 flex-shrink-0" />
            <span className="text-white/60 truncate">{recipe?.name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleFavorite}
            className={`p-1.5 rounded-lg transition-all ${isFavorited ? 'bg-rose-500/20' : 'hover:bg-white/5'}`}
            data-testid="detail-favorite-btn"
          >
            <Heart className={`w-3.5 h-3.5 transition-colors ${isFavorited ? 'text-rose-400 fill-rose-400' : 'text-white/30'}`} />
          </button>
          <span className="text-[10px] text-white/20 hidden sm:inline">{recipe?.pricing?.credits} cr</span>
        </div>
      </header>

      {/* Main Content — Split View */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden" data-testid="main-content-wrapper">
        {/* Left Panel — Preview/Output (~75%, or full-width in Focus Mode) */}
        <div className={`${focusModeActive ? 'lg:flex-1' : 'lg:w-3/4'} lg:overflow-y-auto p-4 sm:p-6 ${(isUrlToAd || isSeedanceDirect || isHappyHorse || isKling || isKling) ? 'flex-none lg:flex-1' : 'flex-1'}`} data-testid="recipe-preview-panel">
          {/* Recipe Info — compact on mobile */}
          <div className="mb-4">
            <div className="flex items-start gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
                <CatIcon className="w-4 h-4" style={{ color: colors.text }} />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-white leading-normal break-words" data-testid="recipe-name">{recipe?.name}</h1>
                <p className="text-[11px] text-white/40 line-clamp-1 sm:line-clamp-2">{recipe?.description}</p>
              </div>
            </div>
            {recipe?.creator && (
              <div className="flex items-center gap-2 mt-2">
                {recipe.creator.verified && (
                  <div className="w-5 h-5 rounded-full bg-[#0EA5E9]/20 flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-[#0EA5E9]" />
                  </div>
                )}
                <span className="text-xs text-white/40">by {recipe.creator.name}</span>
                {recipe.creator.organization && <span className="text-xs text-white/30">({recipe.creator.organization})</span>}
              </div>
            )}
          </div>

          {/* Active Montage Runs (parallel) */}
          {isMontage && activeRuns.length > 0 && !runResult && (
            <div className="mb-4 space-y-3" data-testid="active-runs">
              {activeRuns.filter(r => r.status !== 'completed' || !runResult).map((run) => (
                <div
                  key={run.runId}
                  className="rounded-xl overflow-hidden cursor-pointer"
                  style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${run.status === 'completed' ? 'rgba(34,197,94,0.2)' : run.status === 'failed' ? 'rgba(239,68,68,0.2)' : 'rgba(20,184,166,0.15)'}` }}
                  onClick={() => {
                    if (run.status === 'completed' && run.outputs) {
                      setRunResult({ outputs: run.outputs, status: 'completed', inputs: {} });
                      setActiveRuns(prev => prev.filter(r => r.runId !== run.runId));
                    }
                  }}
                >
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {run.status === 'processing' && <Loader2 className="w-3.5 h-3.5 text-teal-400 animate-spin" />}
                        {run.status === 'completed' && <CheckCircle className="w-3.5 h-3.5 text-green-400" />}
                        {run.status === 'failed' && <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
                        <span className={`text-xs font-medium ${run.status === 'completed' ? 'text-green-400' : run.status === 'failed' ? 'text-red-400' : 'text-teal-400'}`}>
                          {run.status === 'completed' ? 'Ready — tap to view' : run.status === 'failed' ? 'Failed' : run.currentNode || 'Processing...'}
                        </span>
                      </div>
                      <span className="text-[9px] text-white/20">{run.runId.slice(0, 8)}</span>
                      {run.status === 'processing' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            axios.post(`${API}/api/recipes/${slug}/runs/${run.runId}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } })
                              .then(() => {
                                clearInterval(activeRunPolls.current[run.runId]);
                                delete activeRunPolls.current[run.runId];
                                setActiveRuns(prev => prev.map(r => r.runId === run.runId ? { ...r, status: 'failed', error: 'Cancelled by user' } : r));
                                toast.info('Run cancelled');
                              })
                              .catch(() => toast.error('Failed to cancel'));
                          }}
                          className="px-2 py-0.5 rounded text-[9px] text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          data-testid={`cancel-run-${run.runId.slice(0,8)}`}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    {run.status === 'processing' && (
                      <div className="w-full bg-white/5 rounded-full h-1">
                        <div className="h-1 rounded-full transition-all duration-700" style={{ width: `${run.progress || 5}%`, background: 'linear-gradient(90deg, #22D3EE, #14B8A6)' }} />
                      </div>
                    )}
                    {run.status === 'failed' && run.error && (
                      <p className="text-[10px] text-red-400/60 mt-1">{run.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Output Area */}
          {isTVC && (runResult || running) ? (
            /* TVC Auto-Pipeline: 5-stage live progress column + inline Montage timeline */
            <TVCPipelineView
              tvcState={runResult?.tvc_state}
              runStatus={runResult?.status || (running ? 'processing' : 'queued')}
              runId={runResult?.run_id || null}
              token={token}
            />
          ) : isUrlToAd ? (
            /* Admaker video frame — always visible */
            <div className="flex flex-col items-center justify-start px-4 pt-2 w-full" data-testid="ad-video-left-panel">
              <div className="w-full rounded-2xl overflow-hidden" style={{ border: `1px solid ${adVideoUrl ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.06)'}`, boxShadow: adVideoUrl ? '0 8px 32px rgba(16,185,129,0.1)' : 'none' }}>
                <div className={`w-full bg-black relative ${adVideoUrl ? '' : 'aspect-video'}`}>
                  {adVideoUrl ? (
                    <video
                      src={adVideoUrl}
                      controls
                      playsInline
                      preload="auto"
                      className="w-full object-contain mx-auto block" style={{aspectRatio: "16/9"}}
                      data-testid="ad-video-player"
                    />
                  ) : adVideoGenerating ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full border-2 border-emerald-500/20 flex items-center justify-center mx-auto">
                          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                        </div>
                        <p className="text-white/50 text-sm mt-4 font-medium">Generating 15s Ad...</p>
                        <p className="text-white/25 text-xs mt-1">This takes 3-5 minutes</p>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/[0.04]">
                        <div className="h-full bg-emerald-500/40 animate-pulse rounded-full" style={{ width: '60%' }} />
                      </div>
                    </div>
                  ) : (
                    /* Idle — cover poster */
                    <img
                      src="https://customer-assets.emergentagent.com/job_2b898026-3c5b-485e-ad1b-095b1b0d4407/artifacts/xffkmbcr_45A74C97-3A88-42B7-8D8C-F528FE6C0593.png"
                      alt="Admaker"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                </div>
                {adVideoUrl && (
                  <div className="flex items-center justify-between px-4 py-3" style={{ background: 'rgba(16,185,129,0.06)' }}>
                    <span className="text-xs text-emerald-400/80 font-medium">15s Ad Ready</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => resetAdmaker()}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-cyan-400/70 hover:text-cyan-300 transition-all"
                        style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.15)' }}
                        data-testid="new-ad-btn"
                      >
                        <X className="w-3 h-3" /> New Ad
                      </button>
                      <button
                        onClick={() => { setAdVideoUrl(null); setAdVideoGenerating(false); }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white/60 hover:text-white transition-all"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                        data-testid="generate-again-left-btn"
                      >
                        <RefreshCw className="w-3 h-3" /> Again
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const resp = await fetch(`${API}/api/recipes/url-to-ad/download-video?url=${encodeURIComponent(adVideoUrl)}`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            const blob = await resp.blob();
                            const blobUrl = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = blobUrl;
                            a.download = 'admaker-video.mp4';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(blobUrl);
                          } catch { window.open(adVideoUrl, '_blank'); }
                        }}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all"
                        style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
                        data-testid="download-video-btn"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        Download
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : isSeedanceDirect ? (
            /* Seedance Direct video frame — always visible */
            <div className="flex flex-col items-center justify-start px-4 pt-2 w-full" data-testid="sd-video-left-panel">
              {/* Keyframe thumbnail reference */}
              {(sdKeyframeUrl || sdLastFrameUrl) && (
                <div className="w-full flex items-center gap-2 mb-3 px-1">
                  {sdKeyframeUrl && (
                    <div className="flex items-center gap-1.5">
                      <img src={sdKeyframeUrl} alt="First frame" className="w-12 h-12 rounded-lg object-cover" style={{ border: '1px solid rgba(255,255,255,0.1)' }} />
                      <span className="text-[10px] text-white/30">First frame</span>
                    </div>
                  )}
                  {sdLastFrameUrl && (
                    <div className="flex items-center gap-1.5" data-testid="sd-last-frame-thumb-wrap">
                      <img src={sdLastFrameUrl} alt="Last frame" className="w-12 h-12 rounded-lg object-cover" style={{ border: '1px solid rgba(14,165,233,0.25)' }} data-testid="sd-last-frame-thumb" />
                      <span className="text-[10px] text-white/30">Last frame</span>
                    </div>
                  )}
                </div>
              )}
              <div className="w-full rounded-2xl overflow-hidden" style={{ border: `1px solid ${sdVideoUrl ? 'rgba(14,165,233,0.25)' : 'rgba(255,255,255,0.06)'}`, boxShadow: sdVideoUrl ? '0 8px 32px rgba(14,165,233,0.1)' : 'none' }}>
                <div className={`w-full bg-black relative ${sdVideoUrl ? '' : 'aspect-video'}`}>
                  {sdVideoUrl ? (
                    <video
                      src={sdVideoUrl}
                      controls
                      playsInline
                      preload="auto"
                      className="w-full object-contain mx-auto block" style={{aspectRatio: "16/9"}}
                      data-testid="sd-video-player"
                    />
                  ) : sdVideoGenerating ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full border-2 border-cyan-500/20 flex items-center justify-center mx-auto">
                          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                        </div>
                        <p className="text-white/50 text-sm mt-4 font-medium">Generating Video...</p>
                        <p className="text-white/25 text-xs mt-1">This takes 2-5 minutes</p>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/[0.04]">
                        <div className="h-full bg-cyan-500/40 animate-pulse rounded-full" style={{ width: '50%' }} />
                      </div>
                    </div>
                  ) : (
                    /* Idle — upload placeholder */
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0c1220] to-[#111827]">
                      <div className="text-center">
                        <Film className="w-12 h-12 text-cyan-500/20 mx-auto mb-3" />
                        <p className="text-white/30 text-sm font-medium">Seedance</p>
                        <p className="text-white/15 text-xs mt-1">Upload a keyframe and generate</p>
                      </div>
                    </div>
                  )}
                </div>
                {sdVideoUrl && (
                  <div className="flex items-center justify-between px-4 py-3" style={{ background: 'rgba(14,165,233,0.06)' }}>
                    <span className="text-xs text-cyan-400/80 font-medium">Video Ready</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => resetSeedanceDirect()}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-violet-400/70 hover:text-violet-300 transition-all"
                        style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)' }}
                        data-testid="sd-new-btn"
                      >
                        <X className="w-3 h-3" /> New
                      </button>
                      <button
                        onClick={() => { setSdVideoUrl(null); setSdVideoGenerating(false); }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white/60 hover:text-white transition-all"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                        data-testid="sd-again-btn"
                      >
                        <RefreshCw className="w-3 h-3" /> Again
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const resp = await fetch(`${API}/api/recipes/${slug}/download-video?url=${encodeURIComponent(sdVideoUrl)}`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            const blob = await resp.blob();
                            const blobUrl = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = blobUrl;
                            a.download = 'seedance-video.mp4';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(blobUrl);
                          } catch { window.open(sdVideoUrl, '_blank'); }
                        }}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all"
                        style={{ background: 'linear-gradient(135deg, #0EA5E9, #0284C7)' }}
                        data-testid="sd-download-btn"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        Download
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {/* VLM QC Panel for Seedance Direct */}
              {sdVideoUrl && (
                <div className="w-full px-4 mt-3">
                  <VLMQCPanel
                    videoUrl={sdVideoUrl}
                    prompt={sdPrompt}
                    referenceImages={sdKeyframeUrl ? [sdKeyframeUrl] : []}
                    recipeType="seedance"
                    token={token}
                  />
                </div>
              )}
            </div>
          ) : isKling ? (
            /* Kling video frame */
            <div className="flex flex-col items-center justify-start px-4 pt-2 w-full" data-testid="kl-video-left-panel">
              {(klKeyframeUrl || klLastFrameUrl) && (
                <div className="w-full flex items-center gap-3 mb-3 px-1" data-testid="kl-frame-thumbs">
                  {klKeyframeUrl && (
                    <div className="flex items-center gap-2">
                      <img src={klKeyframeUrl} alt="First frame" className="w-12 h-12 rounded-lg object-cover" style={{ border: '1px solid rgba(255,255,255,0.1)' }} data-testid="kl-first-frame-thumb" />
                      <span className="text-[10px] text-white/30">First frame</span>
                    </div>
                  )}
                  {klLastFrameUrl && (
                    <>
                      <span className="text-white/20 text-sm leading-none">→</span>
                      <div className="flex items-center gap-2">
                        <img src={klLastFrameUrl} alt="Last frame" className="w-12 h-12 rounded-lg object-cover" style={{ border: '1px solid rgba(244,63,94,0.25)' }} data-testid="kl-last-frame-thumb" />
                        <span className="text-[10px] text-rose-300/60">Last frame</span>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="w-full rounded-2xl overflow-hidden" style={{ border: `1px solid ${klVideoUrl ? 'rgba(244,63,94,0.25)' : 'rgba(255,255,255,0.06)'}` }}>
                <div className={`w-full bg-black relative ${klVideoUrl ? '' : 'aspect-video'}`}>
                  {klVideoUrl ? (
                    <video src={klVideoUrl} controls playsInline preload="auto" className="w-full object-contain mx-auto block" style={{aspectRatio: "16/9"}} data-testid="kl-video-player" />
                  ) : klVideoGenerating ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full border-2 border-rose-500/20 flex items-center justify-center mx-auto"><Loader2 className="w-8 h-8 text-rose-400 animate-spin" /></div>
                        <p className="text-white/50 text-sm mt-4 font-medium">Generating Video...</p>
                        <p className="text-white/25 text-xs mt-1">Kling — 2-5 minutes</p>
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0c1220] to-[#111827]">
                      <div className="text-center">
                        <Film className="w-12 h-12 text-rose-500/20 mx-auto mb-3" />
                        <p className="text-white/30 text-sm font-medium">Kling</p>
                        <p className="text-white/15 text-xs mt-1">Upload first frame and generate</p>
                      </div>
                    </div>
                  )}
                </div>
                {klVideoUrl && (
                  <div className="flex items-center justify-between px-4 py-3" style={{ background: 'rgba(244,63,94,0.06)' }}>
                    <span className="text-xs text-rose-400/80 font-medium">Video Ready</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setKlVideoUrl(null); setKlVideoGenerating(false); setKlKeyframeUrl(null); setKlLastFrameUrl(null); setKlResetKey(k => k + 1); }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-violet-400/70" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)' }} data-testid="kl-new-btn"><X className="w-3 h-3" /> New</button>
                      <button onClick={() => authDownload(klVideoUrl, 'kling-video.mp4')} className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #F43F5E, #E11D48)' }} data-testid="kl-download-btn"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>Download</button>
                    </div>
                  </div>
                )}
              </div>
              {klVideoUrl && (
                <div className="w-full px-4 mt-3">
                  <VLMQCPanel videoUrl={klVideoUrl} prompt={klPrompt} referenceImages={klKeyframeUrl ? [klKeyframeUrl] : []} recipeType="seedance" token={token} />
                </div>
              )}
            </div>
          ) : isHappyHorse ? (
            /* HappyHorse video frame — same pattern as Seedance */
            <div className="flex flex-col items-center justify-start px-4 pt-2 w-full" data-testid="hh-video-left-panel">
              {hhKeyframeUrl && (
                <div className="w-full flex items-center gap-2 mb-3 px-1">
                  <img src={hhKeyframeUrl} alt="Keyframe" className="w-12 h-12 rounded-lg object-cover" style={{ border: '1px solid rgba(255,255,255,0.1)' }} />
                  <span className="text-[10px] text-white/30">Keyframe reference</span>
                </div>
              )}
              <div className="w-full rounded-2xl overflow-hidden" style={{ border: `1px solid ${hhVideoUrl ? 'rgba(245,158,11,0.25)' : 'rgba(255,255,255,0.06)'}`, boxShadow: hhVideoUrl ? '0 8px 32px rgba(245,158,11,0.1)' : 'none' }}>
                <div className={`w-full bg-black relative ${hhVideoUrl ? '' : 'aspect-video'}`}>
                  {hhVideoUrl ? (
                    <video src={hhVideoUrl} controls playsInline preload="auto" className="w-full object-contain mx-auto block" style={{aspectRatio: "16/9"}} data-testid="hh-video-player" />
                  ) : hhVideoGenerating ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full border-2 border-amber-500/20 flex items-center justify-center mx-auto"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>
                        <p className="text-white/50 text-sm mt-4 font-medium">Generating Video...</p>
                        <p className="text-white/25 text-xs mt-1">HappyHorse 1.0 — 2-10 minutes</p>
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0c1220] to-[#111827]">
                      <div className="text-center">
                        <Film className="w-12 h-12 text-amber-500/20 mx-auto mb-3" />
                        <p className="text-white/30 text-sm font-medium">HappyHorse 1.0</p>
                        <p className="text-white/15 text-xs mt-1">Upload a keyframe and generate</p>
                      </div>
                    </div>
                  )}
                </div>
                {hhVideoUrl && (
                  <div className="flex items-center justify-between px-4 py-3" style={{ background: 'rgba(245,158,11,0.06)' }}>
                    <span className="text-xs text-amber-400/80 font-medium">Video Ready</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setHhVideoUrl(null); setHhVideoGenerating(false); setHhRestoredData(null); setHhKeyframeUrl(null); setHhResetKey(k => k + 1); }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-violet-400/70 hover:text-violet-300 transition-all" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)' }} data-testid="hh-new-btn"><X className="w-3 h-3" /> New</button>
                      <button onClick={async () => { try { const resp = await fetch(`${API}/api/recipes/happyhorse-direct/download-video?url=${encodeURIComponent(hhVideoUrl)}`, { headers: { Authorization: `Bearer ${token}` } }); const blob = await resp.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'happyhorse-video.mp4'; document.body.appendChild(a); a.click(); document.body.removeChild(a); } catch { window.open(hhVideoUrl, '_blank'); } }} className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all" style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }} data-testid="hh-download-btn"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>Download</button>
                    </div>
                  </div>
                )}
              </div>
              {hhVideoUrl && (
                <div className="w-full px-4 mt-3">
                  <VLMQCPanel videoUrl={hhVideoUrl} prompt={hhPrompt} referenceImages={hhKeyframeUrl ? [hhKeyframeUrl] : []} recipeType="seedance" token={token} />
                </div>
              )}
            </div>
          ) : photoshopEditUrl ? (
            /* AI Photoshop — Before/After comparison of the accepted edit */
            <div className="flex flex-col items-center justify-start px-4 pt-2 w-full" data-testid="photoshop-result-panel">
              <div className="w-full rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,240,255,0.2)', boxShadow: '0 8px 32px rgba(0,240,255,0.08)' }}>
                {(() => {
                  // Prefer the freshly uploaded imagePreview (data URL) for
                  // the "before" side. Falling back to the Original Image
                  // output URL keeps the comparison working when users reopen
                  // a completed run from Recent Runs (no imagePreview state).
                  const originalOutput = runResult?.outputs?.find(o => o.type === 'image' && (o.label === 'Original Image' || o.label === 'Original'));
                  const beforeSrc = imagePreview || (originalOutput?.url ? resolveOutputUrl(originalOutput.url) : null);
                  if (beforeSrc) {
                    return (
                      <CompareSlider
                        beforeSrc={beforeSrc}
                        afterSrc={resolveOutputUrl(photoshopEditUrl)}
                        beforeLabel="Original"
                        afterLabel="Edited"
                      />
                    );
                  }
                  return (
                    <img
                      src={photoshopEditUrl}
                      alt="Edited"
                      className="w-full max-h-[70vh] object-contain mx-auto block"
                      style={{ background: '#050A10' }}
                      data-testid="photoshop-edit-image"
                    />
                  );
                })()}
                <div className="flex items-center justify-between px-4 py-3" style={{ background: 'rgba(0,240,255,0.04)' }}>
                  <span className="text-xs font-medium" style={{ color: '#00F0FF' }}>Edit Complete</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setPhotoshopEditUrl(null); setPhotoshopShotId(null); setRunResult(null); }}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-cyan-400/70 hover:text-cyan-300 transition-all"
                      style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.15)' }}
                      data-testid="photoshop-new-btn"
                    >
                      <X className="w-3 h-3" /> New Edit
                    </button>
                    <button
                      onClick={() => {
                        // Re-open workbench to continue editing
                        const wbOutput = runResult?.outputs?.find(o => o.label === 'workbench_shot_id');
                        if (wbOutput?.content) {
                          setPhotoshopEditUrl(null);
                          setPhotoshopShotId(wbOutput.content);
                        }
                      }}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white/60 hover:text-white transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                      data-testid="photoshop-reopen-btn"
                    >
                      <RefreshCw className="w-3 h-3" /> Edit More
                    </button>
                    <a
                      href={photoshopEditUrl}
                      download="ai-photoshop-edit.png"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all"
                      style={{ background: 'linear-gradient(135deg, #0EA5E9, #0284C7)' }}
                      data-testid="photoshop-download-btn"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      Download
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : photoshopShotId ? (
            <div data-testid="photoshop-workbench">
              <GeminiIterationModal
                shotId={photoshopShotId}
                shot={{ id: photoshopShotId, edit_engine: 'gpt-image-2' }}
                mode="photoshop"
                onClose={() => { setPhotoshopShotId(null); }}
                onSelect={(url) => {
                  setPhotoshopEditUrl(url);
                  setPhotoshopShotId(null);
                }}
                initialInstruction={textInput || runResult?.inputs?.text_input || ''}
                onAddToGallery={async (payload) => {
                  try {
                    const res = await axios.post(`${API}/api/ai-photoshop/gallery`, payload);
                    toast.success(res.data?.deduped ? 'Already in Gallery' : 'Added to Gallery ★');
                    setGalleryRefreshKey(k => k + 1);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Could not add to Gallery');
                  }
                }}
              />
            </div>
          ) : runResult && runResult.outputs?.length > 0 && !isUrlToAd && !isSeedanceDirect && !isHappyHorse && !isKling ? (
            <div data-testid="recipe-outputs" ref={outputsRef}>
              <div className="flex items-center gap-2 mb-4">
                {runResult.status === 'failed' ? (
                  <>
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-medium text-amber-400">Completed with issues</span>
                  </>
                ) : isMontage && runResult.seedance_job_id && !montageVideoReady ? (
                  <>
                    <Loader2 className="w-4 h-4 text-teal-400 animate-spin" />
                    <span className="text-sm font-medium text-teal-400">Video Generating</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 text-[#22C55E]" />
                    <span className="text-sm font-medium text-[#22C55E]">Recipe Complete</span>
                  </>
                )}
                <span className="text-xs text-white/30">({runResult.outputs.length} output{runResult.outputs.length > 1 ? 's' : ''})</span>
              </div>
              {/* Before/After Comparison — when input image + image output exist.
                  Skipped for AI Photoshop because its recipe outputs only contain
                  the Original image (the real edit lives on the virtual workbench
                  shot), which would make the slider show the original on both sides. */}
              {!isMontage && !isAiPhotoshop && imagePreview && runResult.outputs?.some(o => o.type === 'image' && o.url) && (
                <div className="mb-4">
                  <span className="text-[10px] text-white/40 block mb-2">Before / After</span>
                  <CompareSlider
                    beforeSrc={imagePreview}
                    afterSrc={resolveOutputUrl(runResult.outputs.find(o => o.type === 'image' && o.url).url)}
                    beforeLabel="Original"
                    afterLabel={runResult.outputs.find(o => o.type === 'image' && o.url)?.label || "Result"}
                  />
                </div>
              )}
              {isMontage ? (
                <>
                <MontageOutputView
                  outputs={runResult.outputs}
                  seedanceJobId={runResult.seedance_job_id}
                  onDownload={authDownload}
                  resolveUrl={resolveOutputUrl}
                  ImageComponent={AuthImage}
                  onVideoReady={() => setMontageVideoReady(true)}
                  onRerun={async () => {
                    // Pre-fill slots from the completed run's outputs
                    const outputs = runResult?.outputs || [];
                    const findImgUrl = (label) => {
                      const o = outputs.find(x => x.type === 'image' && x.label?.toLowerCase().includes(label));
                      if (!o) return null;
                      return o.url?.startsWith('http') ? o.url : `${process.env.REACT_APP_BACKEND_URL}${o.url}`;
                    };

                    // Fetch images with auth and convert to data URLs
                    const fetchAsDataUrl = async (url) => {
                      if (!url) return null;
                      try {
                        const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                        if (!resp.ok) return url; // fallback to raw URL
                        const blob = await resp.blob();
                        return new Promise((resolve) => {
                          const reader = new FileReader();
                          reader.onload = () => resolve(reader.result);
                          reader.readAsDataURL(blob);
                        });
                      } catch { return url; }
                    };

                    const [castPreview, costumePreview, envPreview, skuPreview] = await Promise.all([
                      fetchAsDataUrl(findImgUrl('cast')),
                      fetchAsDataUrl(findImgUrl('costume')),
                      fetchAsDataUrl(findImgUrl('environment')),
                      fetchAsDataUrl(findImgUrl('sku')),
                    ]);

                    setMontageSlots(prev => ({
                      ...prev,
                      cast: castPreview ? { ...prev.cast, preview: castPreview, processedUrl: findImgUrl('cast') } : prev.cast,
                      costume: costumePreview ? { ...prev.costume, preview: costumePreview, processedUrl: findImgUrl('costume') } : prev.costume,
                      environment: envPreview ? { ...prev.environment, preview: envPreview, processedUrl: findImgUrl('environment') } : prev.environment,
                      sku: skuPreview ? { ...prev.sku, preview: skuPreview, processedUrl: findImgUrl('sku') } : prev.sku,
                    }));
                    // Restore prompt
                    const runPrompt = runResult?.inputs?.text_input || '';
                    setTextInput(runPrompt);
                    setMontageSlots(prev => ({ ...prev, manualPrompt: runPrompt }));
                    // Restore dropdown
                    if (runResult?.inputs?.dropdown_value) setDropdownValue(runResult.inputs.dropdown_value);
                    setRunResult(null);
                    setRunning(false);
                    setShowMobileInputs(true);
                  }}
                  scenePrompt={runResult?.inputs?.text_input || (promptMode === 'manual' ? montageSlots.manualPrompt : textInput) || ''}
                  onPromptChange={(val) => { setTextInput(val); setMontageSlots(prev => ({ ...prev, manualPrompt: val })); }}
                />
                {/* VLM QC Panel for Montage */}
                {montageVideoReady && runResult?.outputs?.find(o => o.type === 'video')?.url && (
                  <div className="mt-3">
                    <VLMQCPanel
                      videoUrl={(() => { const v = runResult.outputs.find(o => o.type === 'video'); return v?.url?.startsWith('http') ? v.url : `${process.env.REACT_APP_BACKEND_URL}${v?.url}`; })()}
                      prompt={runResult?.inputs?.text_input || textInput || ''}
                      referenceImages={Object.values(montageSlots).filter(s => s?.processedUrl).map(s => s.processedUrl)}
                      recipeType="montage"
                      slotLabels={Object.keys(montageSlots).filter(k => montageSlots[k]?.processedUrl && k !== 'aspectRatio' && k !== 'manualPrompt' && k !== 'keywords')}
                      token={token}
                    />
                  </div>
                )}
                </>
              ) : isScriptNormalizer && runResult.outputs.find(o => o.type === 'json' && o.content?.polished_script) ? (
                <ScriptNormalizerOutput bundle={runResult.outputs.find(o => o.type === 'json' && o.content?.polished_script)?.content} />
              ) : (
              <div className={`grid gap-4 ${runResult.outputs.filter(o => o.type !== 'text').length === 1 ? 'grid-cols-1 max-w-2xl' : runResult.outputs.filter(o => o.type !== 'text').length <= 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`}>
                {runResult.outputs.map((output, i) => (
                  <div key={i} className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                    data-testid={`output-${i}`}>
                    {output.type === 'image' ? (
                      <>
                        <div className="relative cursor-pointer group" onClick={() => openLightbox(resolveOutputUrl(output.url))}>
                          <AuthImage src={resolveOutputUrl(output.url)} alt={output.label} className="w-full aspect-video object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                        <div className="p-2 flex items-center justify-between">
                          <span className="text-[10px] text-white/60">{output.label}</span>
                          <button onClick={() => authDownload(resolveOutputUrl(output.url), output.label.replace(/[^a-zA-Z0-9.-]/g, '_') + '.png')} 
                            className="p-1 hover:bg-white/10 rounded transition-colors" data-testid={`download-output-${i}`}>
                            <Download className="w-3 h-3 text-white/40" />
                          </button>
                        </div>
                      </>
                    ) : output.type === 'video' ? (
                      <>
                        <AuthVideo src={resolveOutputUrl(output.url)} controls className="w-full aspect-video bg-black" />
                        <div className="p-2 flex items-center justify-between">
                          <span className="text-[10px] text-white/60">{output.label}</span>
                          <button onClick={() => authDownload(resolveOutputUrl(output.url), output.url.split('/').pop())}
                            className="p-1 hover:bg-white/10 rounded transition-colors">
                            <Download className="w-3 h-3 text-white/40" />
                          </button>
                        </div>
                      </>
                    ) : output.type === 'text' ? (
                      <TextOutputCard output={output} i={i} />
                    ) : output.type === 'download' ? (
                      <div className="p-3">
                        <span className="text-[10px] text-white/40 block mb-2">{output.label}</span>
                        <button onClick={() => authDownload(resolveOutputUrl(output.url), output.url.split('/').pop())}
                          className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 hover:bg-[#0EA5E9]/20 transition-colors w-full text-left"
                          data-testid={`download-file-${i}`}
                        >
                          <FileVideo className="w-4 h-4 text-[#0EA5E9]" />
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-white block truncate">{output.label}</span>
                            {output.content && <span className="text-[9px] text-white/40 block">{output.content}</span>}
                          </div>
                          <Download className="w-3.5 h-3.5 text-[#0EA5E9]" />
                        </button>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setRunResult(null); }}
                  className="px-4 py-2 rounded-lg text-xs font-medium bg-white/[0.05] border border-white/[0.08] text-white/70 hover:bg-white/[0.08] transition-colors flex items-center gap-1.5"
                  data-testid="run-again-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Generate Again
                </button>
              </div>
            </div>
          ) : running ? (
            isMontage ? (
            <>
              <MontageOutputView
                outputs={[
                  ...(montageSlots.cast?.preview || montageSlots.cast?.processedUrl ? [{ label: 'Cast', type: 'image', url: montageSlots.cast.preview || montageSlots.cast.processedUrl, isDataUrl: !!montageSlots.cast?.preview?.startsWith?.('data:') }] : []),
                  ...(montageSlots.costume?.preview || montageSlots.costume?.processedUrl ? [{ label: 'Costume', type: 'image', url: montageSlots.costume.preview || montageSlots.costume.processedUrl, isDataUrl: !!montageSlots.costume?.preview?.startsWith?.('data:') }] : []),
                  ...(montageSlots.environment?.preview || montageSlots.environment?.processedUrl ? [{ label: 'Environment', type: 'image', url: montageSlots.environment.preview || montageSlots.environment.processedUrl, isDataUrl: !!montageSlots.environment?.preview?.startsWith?.('data:') }] : []),
                  ...(montageSlots.sku?.preview || montageSlots.sku?.processedUrl ? [{ label: 'SKU', type: 'image', url: montageSlots.sku.preview || montageSlots.sku.processedUrl, isDataUrl: !!montageSlots.sku?.preview?.startsWith?.('data:') }] : []),
                ]}
                resolveUrl={(u) => u?.startsWith?.('data:') ? u : resolveOutputUrl(u)}
                ImageComponent="img"
                processingInfo={{ currentNode, progressPct }}
                scenePrompt={promptMode === 'manual' ? (montageSlots.manualPrompt || '') : ''}
                onPromptChange={(val) => setMontageSlots(prev => ({ ...prev, manualPrompt: val }))}
                onRerun={runRecipe}
              />
              {/* Back to runs button during processing */}
              <button
                onClick={() => { setRunning(false); setRunResult(null); }}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-[11px] font-medium text-white/30 hover:text-white/50 transition-colors"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                data-testid="back-to-runs-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Inputs & History
              </button>
            </>
            ) : (
            <div className="flex flex-col items-center justify-center py-20" data-testid="recipe-processing">
              {/* Circular progress ring with percentage */}
              <div className="relative w-24 h-24 mb-4">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                  {/* Background ring */}
                  <circle cx="48" cy="48" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                  {/* Progress ring */}
                  <circle
                    cx="48" cy="48" r="42" fill="none"
                    stroke="#0EA5E9" strokeWidth="4" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 42}`}
                    strokeDashoffset={`${2 * Math.PI * 42 * (1 - progressPct / 100)}`}
                    style={{ transition: 'stroke-dashoffset 0.8s ease-in-out' }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-mono font-bold text-[#0EA5E9]" data-testid="progress-pct">
                    {progressPct}%
                  </span>
                </div>
              </div>
              <p className="text-sm text-white/70 mb-1">Processing your recipe...</p>
              {currentNode && (
                <div className="flex items-center gap-2 mb-1">
                  <Loader2 className="w-3 h-3 animate-spin text-[#0EA5E9]" />
                  <p className="text-xs text-[#0EA5E9] font-mono">{currentNode}</p>
                </div>
              )}
              {/* Progress bar */}
              <div className="w-48 h-1 rounded-full bg-white/[0.06] mt-2 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#0EA5E9]"
                  style={{ width: `${progressPct}%`, transition: 'width 0.8s ease-in-out' }}
                />
              </div>
              <p className="text-xs text-white/30 mt-2">Estimated time: ~{recipe?.pricing?.estimated_seconds}s</p>
            </div>
            )
          ) : (
            /* Empty state with info */
            <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="recipe-empty-state">
              {recipe?.thumbnail_url ? (
                <div className="relative w-full aspect-video rounded-2xl overflow-hidden mb-4 shadow-2xl" style={{ border: `1px solid ${colors.border}` }}>
                  <img
                    src={recipe.thumbnail_url}
                    alt={recipe?.name || 'Recipe poster'}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4 text-left">
                    <p className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-1" style={{ color: colors.text }}>
                      {recipe?.category || 'Recipe'}
                    </p>
                    <p className="text-base sm:text-lg font-bold text-white truncate">{recipe?.name}</p>
                  </div>
                </div>
              ) : (
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
                  <CatIcon className="w-8 h-8" style={{ color: colors.text }} />
                </div>
              )}
              <p className="text-sm text-white/50 mb-1">Configure your inputs <span className="hidden lg:inline">on the right panel</span><span className="lg:hidden">below</span></p>
              <p className="text-xs text-white/30">then click Generate to run this recipe</p>

              {/* Expected outputs preview */}
              <div className="mt-6 max-w-md w-full">
                <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Expected Outputs</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {(recipe?.outputs || []).map((out, i) => (
                    <span key={i} className="px-2 py-1 rounded-md text-[10px] bg-white/[0.04] border border-white/[0.06] text-white/40">
                      {out.type === 'image' ? <ImageIcon className="w-3 h-3 inline mr-1" /> : <FileText className="w-3 h-3 inline mr-1" />}
                      {out.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && !running && (
            <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-2" data-testid="recipe-error">
              <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-red-300 font-medium mb-0.5">Generation Failed</p>
                <p className="text-xs text-red-300/70">{error}</p>
              </div>
            </div>
          )}

          {/* Montage Timeline Editor (DESKTOP) — lives in the wide left panel
              so trim handles, zoom, and clip drag have room to breathe. On
              < lg viewports this block doesn't mount; the right-panel copy
              below takes over. Only one instance ever exists at a time —
              no draft fetch dupes, no divergent clip state. */}
          {isMontage && isLgViewport && (
            <div className="mt-4 pt-4 border-t border-white/[0.06]" data-testid="montage-timeline-wrap-left">
              {/* Focus Mode toggle — collapses the right input rail so the
                  timeline gets the full viewport width, Premiere/DaVinci style. */}
              <div className="flex items-center justify-end mb-2">
                <button
                  type="button"
                  onClick={toggleFocusMode}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all border ${focusModeActive ? 'bg-ai-accent/20 text-ai-accent border-ai-accent/30 hover:bg-ai-accent/30' : 'bg-white/[0.04] text-white/50 border-white/[0.08] hover:text-white/80 hover:bg-white/[0.08]'}`}
                  data-testid="toggle-focus-mode-btn"
                  title={focusModeActive ? 'Exit Focus Mode — show inputs panel' : 'Focus Mode — hide inputs, maximise timeline'}
                >
                  <Maximize2 className="w-3 h-3" />
                  {focusModeActive ? 'Exit Focus' : 'Focus Mode'}
                </button>
              </div>
              <MontageTimelinePanel token={token} runId={runResult?.run_id || null} />
            </div>
          )}

          {/* Run History - removed from here, moved to right panel */}
        </div>

        {/* Right Panel — Inputs (~25%, or 48px rail in Focus Mode) */}
        <div className={`${focusModeActive ? 'lg:w-12' : 'lg:w-80 xl:w-96'} border-t lg:border-t-0 lg:border-l border-white/[0.06] flex-shrink-0 flex flex-col transition-[width] duration-200`}
          style={{ background: 'rgba(11, 15, 25, 0.5)' }}
          data-testid="recipe-input-panel"
        >
          {/* Focus-mode rail — click to expand inputs back */}
          {focusModeActive && (
            <button
              type="button"
              onClick={toggleFocusMode}
              className="hidden lg:flex w-full flex-col items-center gap-3 py-3 text-white/40 hover:text-white/80 transition-colors border-b border-white/[0.06]"
              data-testid="focus-mode-rail-expand"
              title="Show inputs panel"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="text-[9px] uppercase tracking-wider" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Inputs</span>
            </button>
          )}
          {/* Mobile collapse toggle when results are showing or montage is processing */}
          {!focusModeActive && ((runResult && runResult.outputs?.length > 0) || (running && isMontage) || (isUrlToAd && (adVideoGenerating || adVideoUrl)) || (isSeedanceDirect && (sdVideoGenerating || sdVideoUrl)) || (isHappyHorse && (hhVideoGenerating || hhVideoUrl)) || (isKling && (klVideoGenerating || klVideoUrl))) && (
            <button 
              onClick={() => setShowMobileInputs(!showMobileInputs)}
              className="lg:hidden p-3 flex items-center justify-between border-b border-white/[0.06] w-full text-left"
              data-testid="toggle-mobile-inputs"
            >
              <span className="text-xs text-white/50 font-medium">Inputs & Settings</span>
              <ChevronDown className={`w-3.5 h-3.5 text-white/30 transition-transform ${showMobileInputs ? 'rotate-180' : ''}`} />
            </button>
          )}
          <div className={`p-4 flex-1 lg:overflow-y-auto pb-28 lg:pb-4 ${focusModeActive ? 'hidden lg:hidden' : ''} ${(isUrlToAd && (adVideoGenerating || adVideoUrl) && !showMobileInputs) ? 'hidden lg:block' : ''} ${(isSeedanceDirect && (sdVideoGenerating || sdVideoUrl) && !showMobileInputs) ? 'hidden lg:block' : ''} ${(isHappyHorse && (hhVideoGenerating || hhVideoUrl) && !showMobileInputs) ? 'hidden lg:block' : ''} ${(isKling && (klVideoGenerating || klVideoUrl) && !showMobileInputs) ? 'hidden lg:block' : ''} ${(!isMontage && !isUrlToAd && !isSeedanceDirect && !isHappyHorse && !isKling && ((runResult && runResult.outputs?.length > 0) || running)) && !showMobileInputs ? 'hidden lg:block' : ''}`}>
            {/* Credit cost — hidden for montage on mobile */}
            <details className={`mb-4 group ${isMontage || isUrlToAd || isSeedanceDirect || isHappyHorse || isKling ? 'hidden' : ''}`}>
              <summary className="p-2.5 rounded-xl cursor-pointer list-none flex items-center justify-between" style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-white/40">Cost</span>
                  <span className="text-xs font-bold" style={{ color: colors.text }}>{recipe?.pricing?.credits} credits</span>
                  <span className="text-[9px] text-white/25">~{recipe?.pricing?.estimated_seconds}s</span>
                </div>
                <ChevronDown className="w-3 h-3 text-white/30 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-3 pb-3 rounded-b-xl -mt-1" style={{ background: colors.bg }}>
                {recipe?.pricing?.estimated_cost_usd != null && (
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-white/40">Estimated API cost</span>
                    <span className="text-[10px] text-emerald-400/80">~${recipe.pricing.estimated_cost_usd.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-white/40">Estimated time</span>
                  <span className="text-[10px] text-white/50">~{recipe?.pricing?.estimated_seconds}s</span>
                </div>
                <div className="mt-2 pt-2 border-t border-white/[0.06]">
                  <span className="text-[9px] text-white/25">1 credit ≈ $0.001 USD</span>
                </div>
              </div>
            </details>

            {/* Presets */}
            {recipe?.presets?.length > 0 && !isMontage && !isUrlToAd && !isSeedanceDirect && !isHappyHorse && !isKling && (
              <div className="mb-4">
                <span className="text-[10px] text-white/40 uppercase tracking-wider block mb-2">Quick Presets</span>
                <div className="flex flex-wrap gap-2">
                  {recipe.presets.map((preset, i) => {
                    const isActive = preset.inputs?.dropdown_value && dropdownValue === preset.inputs.dropdown_value;
                    return (
                      <button
                        key={i}
                        onClick={() => applyPreset(preset)}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all flex items-center gap-1 ${
                          isActive 
                            ? 'bg-[#0EA5E9]/15 border-[#0EA5E9]/40 text-[#0EA5E9]' 
                            : 'bg-white/[0.05] border-white/[0.08] text-white/60 hover:bg-white/[0.08] hover:text-white'
                        }`}
                        style={{ border: '1px solid' }}
                        data-testid={`preset-${i}`}
                        title={preset.description || `Apply ${preset.name} preset`}
                      >
                        <Zap className="w-3 h-3 text-yellow-400" />
                        {preset.name}
                        {isActive && <CheckCircle className="w-3 h-3 ml-0.5" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Dynamic Inputs */}
            <div className="space-y-4">
              {isUrlToAd ? (
                <URLtoAdInputs key={adResetKey} values={montageSlots} onChange={setMontageSlots} token={token} onGenerate={() => {}} onVideoStateChange={(generating, url) => { setAdVideoGenerating(generating); if (url) setAdVideoUrl(url); }} restoredData={adRestoredData} onReset={resetAdmaker} />
              ) : isSeedance25 ? (
                <Seedance25DirectInputs
                  key={sdResetKey}
                  token={token}
                  onVideoStateChange={(generating, url, keyframeUrl, promptText) => {
                    setSdVideoGenerating(generating);
                    if (url) setSdVideoUrl(url);
                    if (keyframeUrl) setSdKeyframeUrl(keyframeUrl);
                    if (promptText) setSdPrompt(promptText);
                  }}
                  restoredData={sdRestoredData}
                />
              ) : isSeedanceDirect ? (
                <SeedanceDirectInputs
                  key={sdResetKey}
                  token={token}
                  onVideoStateChange={(generating, url, keyframeUrl, promptText, lastFrameUrl) => {
                    setSdVideoGenerating(generating);
                    if (url) setSdVideoUrl(url);
                    if (keyframeUrl) setSdKeyframeUrl(keyframeUrl);
                    if (promptText) setSdPrompt(promptText);
                    if (lastFrameUrl !== undefined) setSdLastFrameUrl(lastFrameUrl);
                  }}
                  restoredData={sdRestoredData}
                />
              ) : isHappyHorse ? (
                <HappyHorseDirectInputs
                  key={hhResetKey}
                  token={token}
                  onVideoStateChange={(generating, url, keyframeUrl, promptText) => {
                    setHhVideoGenerating(generating);
                    if (url) setHhVideoUrl(url);
                    if (keyframeUrl) setHhKeyframeUrl(keyframeUrl);
                    if (promptText) setHhPrompt(promptText);
                  }}
                  restoredData={hhRestoredData}
                />
              ) : isKling ? (
                <KlingDirectInputs
                  key={klResetKey}
                  token={token}
                  onVideoStateChange={(generating, url, keyframeUrl, promptText, lastFrameUrl) => {
                    setKlVideoGenerating(generating);
                    if (url) setKlVideoUrl(url);
                    if (keyframeUrl) setKlKeyframeUrl(keyframeUrl);
                    if (promptText) setKlPrompt(promptText);
                    // Pass `undefined` to leave unchanged; `null` to clear; any truthy to set.
                    if (lastFrameUrl !== undefined) setKlLastFrameUrl(lastFrameUrl);
                  }}
                  restoredData={klRestoredData}
                />
              ) : isMontage ? (
                <>
                  {/* Montage Video: Cast / Costume / Environment cards */}
                  <MontageVideoInputs values={montageSlots} onChange={setMontageSlots} token={token} onReprocessSlot={reprocessSingleSlot} />

                  {/* Process button — above prompt mode toggle */}
                  {!slotsProcessed && (montageSlots.cast?.file || montageSlots.costume?.file || montageSlots.environment?.file || montageSlots.sku?.file || montageSlots.cast?.processedUrl || montageSlots.costume?.processedUrl || montageSlots.environment?.processedUrl || montageSlots.sku?.processedUrl) && (
                    <button
                      onClick={processSlots}
                      disabled={processing}
                      className="hidden lg:flex w-full py-2.5 rounded-xl text-xs font-semibold text-white transition-all disabled:opacity-60 items-center justify-center gap-2"
                      style={{ background: processing ? 'rgba(168,85,247,0.15)' : 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)', boxShadow: processing ? 'none' : '0 4px 12px rgba(168, 85, 247, 0.25)' }}
                      data-testid="process-btn-inline"
                    >
                      {processing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing...</> : <><Wand2 className="w-3.5 h-3.5" /> Process Images</>}
                    </button>
                  )}
                  {slotsProcessed && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.12)' }}>
                      <CheckCircle className="w-3 h-3 text-green-400" />
                      <span className="text-[10px] text-green-400/70">Processed — review in slots above, iterate if needed, then generate</span>
                    </div>
                  )}

                  {/* Prompt Mode Toggle */}
                  <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                    <button
                      type="button"
                      onClick={() => setPromptMode('auto')}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium transition-all ${
                        promptMode === 'auto'
                          ? 'bg-teal-500/15 text-teal-400 border-r border-teal-500/20'
                          : 'bg-white/[0.02] text-white/30 hover:text-white/50 border-r border-white/[0.06]'
                      }`}
                      data-testid="prompt-mode-auto"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      I'm Feeling Lucky
                    </button>
                    <button
                      type="button"
                      onClick={() => setPromptMode('manual')}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium transition-all ${
                        promptMode === 'manual'
                          ? 'bg-violet-500/15 text-violet-400'
                          : 'bg-white/[0.02] text-white/30 hover:text-white/50'
                      }`}
                      data-testid="prompt-mode-manual"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Write My Own
                    </button>
                  </div>

                  {promptMode === 'auto' ? (
                  /* Style Engine Controls */
                  <div className="space-y-3" data-testid="style-engine-controls">
                    {/* Style Dropdown */}
                    <div>
                      <label className="text-xs text-white/60 block mb-1.5">Style<span className="text-red-400 ml-0.5">*</span></label>
                      <div className="relative">
                        <select
                          value={textInput || 'Streetwear Fashion Reel'}
                          onChange={e => setTextInput(e.target.value)}
                          className="w-full px-3 py-2.5 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white focus:outline-none focus:border-[#0EA5E9]/40 appearance-none cursor-pointer"
                          data-testid="style-dropdown"
                        >
                          <option value="Streetwear Fashion Reel" className="bg-[#0B0F19]">Streetwear Fashion Reel</option>
                          <option value="Premium Fashion Ad" className="bg-[#0B0F19]">Premium Fashion Ad</option>
                          <option value="Travel Lifestyle Reel" className="bg-[#0B0F19]">Travel Lifestyle Reel</option>
                          <option value="Music Video Montage" className="bg-[#0B0F19]">Music Video Montage</option>
                          <option value="Sports Energy Reel" className="bg-[#0B0F19]">Sports Energy Reel</option>
                          <option value="Luxury Product Film" className="bg-[#0B0F19]">Luxury Product Film</option>
                          <option value="Youth Culture Reel" className="bg-[#0B0F19]">Youth Culture Reel</option>
                          <option value="Cinematic Narrative" className="bg-[#0B0F19]">Cinematic Narrative</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                      </div>
                    </div>

                    {/* Duration + AR Dropdown */}
                    {(recipe?.inputs || []).filter(i => i.type === 'dropdown').map(input => (
                      <div key={input.name}>
                        <label className="text-xs text-white/60 block mb-1.5">{input.label}</label>
                        <div className="relative">
                          <select
                            value={dropdownValue}
                            onChange={e => setDropdownValue(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white focus:outline-none focus:border-[#0EA5E9]/40 appearance-none cursor-pointer"
                            data-testid="dropdown-input"
                          >
                            {(input.options || []).map(opt => (
                              <option key={opt} value={opt} className="bg-[#0B0F19] text-white">{opt}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                        </div>
                      </div>
                    ))}

                    {/* Pacing Dropdown — Optional */}
                    <div>
                      <label className="text-xs text-white/60 block mb-1.5">Pacing <span className="text-[9px] text-white/25">(optional)</span></label>
                      <div className="relative">
                        <select
                          value={textInput2 || ''}
                          onChange={e => setTextInput2(e.target.value)}
                          className="w-full px-3 py-2.5 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white focus:outline-none focus:border-[#0EA5E9]/40 appearance-none cursor-pointer"
                          data-testid="pacing-dropdown"
                        >
                          <option value="" className="bg-[#0B0F19]">Auto (based on style)</option>
                          <option value="Slow and Premium" className="bg-[#0B0F19]">Slow and Premium</option>
                          <option value="Balanced Rhythm" className="bg-[#0B0F19]">Balanced Rhythm</option>
                          <option value="Fast Cuts" className="bg-[#0B0F19]">Fast Cuts</option>
                          <option value="High Energy" className="bg-[#0B0F19]">High Energy</option>
                          <option value="Fast Cuts + Hero Slow Motion" className="bg-[#0B0F19]">Fast Cuts + Hero Slow Motion</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                      </div>
                    </div>

                    {/* Mood / Music — Optional */}
                    <div>
                      <label className="text-xs text-white/60 block mb-1.5">Mood / Music <span className="text-[9px] text-white/25">(optional)</span></label>
                      <input
                        type="text"
                        value={montageSlots.mood || ''}
                        onChange={e => setMontageSlots(prev => ({ ...prev, mood: e.target.value }))}
                        placeholder="e.g. hard modern hip-hop, premium electronic, raw youth vibe"
                        className="w-full px-3 py-2.5 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/20 focus:outline-none focus:border-[#0EA5E9]/40"
                        data-testid="mood-input"
                      />
                    </div>

                    {/* Keywords — Optional free text */}
                    <div>
                      <label className="text-xs text-white/60 block mb-1.5">Keywords <span className="text-[9px] text-white/25">(optional)</span></label>
                      <input
                        type="text"
                        value={montageSlots.keywords || ''}
                        onChange={e => setMontageSlots(prev => ({ ...prev, keywords: e.target.value }))}
                        placeholder="e.g. handheld, selfie, bullet time, sneaker close-ups, luxury, youthful"
                        className="w-full px-3 py-2.5 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/20 focus:outline-none focus:border-[#0EA5E9]/40"
                        data-testid="keywords-input"
                      />
                    </div>
                  </div>
                  ) : (
                  /* Manual Prompt Mode */
                  <div className="space-y-3" data-testid="manual-prompt-controls">
                    <div>
                      <label className="text-xs text-white/60 block mb-1.5">Your Prompt</label>
                      <textarea
                        value={montageSlots.manualPrompt || ''}
                        onChange={e => setMontageSlots(prev => ({ ...prev, manualPrompt: e.target.value }))}
                        placeholder="Create a hyper-real 15-second cinematic streetwear reel featuring..."
                        rows={5}
                        className="w-full px-3 py-2 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/20 focus:outline-none focus:border-[#0EA5E9]/40 resize-none"
                        data-testid="manual-prompt-input"
                      />
                      <p className="text-[9px] text-white/25 mt-1">Your prompt will be sent directly to the video engine with the realism directive appended.</p>
                    </div>

                    {/* Duration dropdown still needed in manual mode */}
                    {(recipe?.inputs || []).filter(i => i.type === 'dropdown').map(input => (
                      <div key={input.name}>
                        <label className="text-xs text-white/60 block mb-1.5">{input.label}</label>
                        <div className="relative">
                          <select
                            value={dropdownValue}
                            onChange={e => setDropdownValue(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white focus:outline-none focus:border-[#0EA5E9]/40 appearance-none cursor-pointer"
                            data-testid="dropdown-input-manual"
                          >
                            {(input.options || []).map(opt => (
                              <option key={opt} value={opt} className="bg-[#0B0F19] text-white">{opt}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </>
              ) : (
              /* Generic inputs for non-montage recipes */
              (recipe?.inputs || []).filter(input => {
                if (input.type === 'text' && input.show_when) return shouldShowInput(input);
                return true;
              }).map((input, i) => (
                <div key={input.name} data-testid={`input-${input.name}`}>
                  <label className="text-xs text-white/60 block mb-1.5">
                    {input.label}
                    {input.required && <span className="text-red-400 ml-0.5">*</span>}
                  </label>
                  {input.description && <p className="text-[9px] text-white/30 mb-1.5">{input.description}</p>}

                  {input.type === 'image' && input.name === 'image' && (
                    <div>
                      <input type="file" ref={fileRef} accept={input.accept || "image/*"} onChange={(e) => handleImageSelect(e, 1)} className="hidden" />
                      {imagePreview ? (
                        <div className="relative rounded-lg overflow-hidden">
                          {isVideoFile(imageFile) ? (
                            <>
                              <video src={imagePreview} controls className="w-full max-h-48 sm:max-h-64 lg:max-h-none lg:aspect-video bg-black object-contain" data-testid="video-preview-1" />
                              {videoDuration && (
                                <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/70 text-[10px] text-white/80 flex items-center gap-1">
                                  {videoDuration.toFixed(1)}s
                                  {videoDuration > (recipe?.max_video_duration || 8) && (
                                    <span className="text-red-400">(max {recipe?.max_video_duration || 8}s)</span>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <img src={imagePreview} alt="Upload" className="w-full max-h-48 sm:max-h-64 lg:max-h-80 object-contain" />
                          )}
                          <button onClick={() => { setImageFile(null); setImagePreview(null); setVideoDuration(null); if(fileRef.current) fileRef.current.value=''; }}
                            className="absolute top-2 right-2 p-1 bg-black/60 rounded-full hover:bg-black/80">
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => fileRef.current?.click()}
                          className="w-full py-8 rounded-xl border-2 border-dashed border-white/[0.08] hover:border-[#0EA5E9]/30 transition-colors flex flex-col items-center gap-2 group"
                          data-testid="upload-image-1"
                        >
                          <Upload className="w-6 h-6 text-white/20 group-hover:text-[#0EA5E9]/50" />
                          <span className="text-xs text-white/30 group-hover:text-white/50">{input.accept?.includes('video') ? 'Upload image or video' : 'Upload image'}</span>
                          <span className="text-[9px] text-white/20">{input.accept?.includes('video') ? 'Max 50MB, 8s for video' : 'JPG, PNG, WebP'}</span>
                        </button>
                      )}
                      {/* AI Photoshop — let users reuse any keyframe from
                          their own projects (or ones they collaborate on)
                          as the input image. Reusable picker so other
                          image-input recipes can opt in later. */}
                      {isAiPhotoshop && !imagePreview && (
                        <button
                          onClick={() => setPickerOpen(true)}
                          className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] text-cyan-400/70 hover:text-cyan-300 transition-colors"
                          data-testid="open-keyframe-picker-btn"
                        >
                          <FolderOpen className="w-3 h-3" />
                          Or pick from your projects
                        </button>
                      )}
                    </div>
                  )}

                  {input.type === 'image' && input.name === 'image2' && (
                    <div>
                      <input type="file" ref={file2Ref} accept={input.accept || "image/*"} onChange={(e) => handleImageSelect(e, 2)} className="hidden" />
                      {image2Preview ? (
                        <div className="relative rounded-lg overflow-hidden">
                          {isVideoFile(image2File) ? (
                            <>
                              <video src={image2Preview} controls className="w-full max-h-48 sm:max-h-64 lg:max-h-none lg:aspect-video bg-black object-contain" data-testid="video-preview-2" />
                              {video2Duration && (
                                <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/70 text-[10px] text-white/80 flex items-center gap-1">
                                  {video2Duration.toFixed(1)}s
                                  {video2Duration > (recipe?.max_video_duration || 8) && (
                                    <span className="text-red-400">(max {recipe?.max_video_duration || 8}s)</span>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <img src={image2Preview} alt="Upload 2" className="w-full max-h-48 sm:max-h-64 lg:max-h-80 object-contain" />
                          )}
                          <button onClick={() => { setImage2File(null); setImage2Preview(null); setVideo2Duration(null); if(file2Ref.current) file2Ref.current.value=''; }}
                            className="absolute top-2 right-2 p-1 bg-black/60 rounded-full hover:bg-black/80">
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => file2Ref.current?.click()}
                          className="w-full py-8 rounded-xl border-2 border-dashed border-white/[0.08] hover:border-[#0EA5E9]/30 transition-colors flex flex-col items-center gap-2 group"
                          data-testid="upload-image-2"
                        >
                          <Upload className="w-6 h-6 text-white/20 group-hover:text-[#0EA5E9]/50" />
                          <span className="text-xs text-white/30 group-hover:text-white/50">{input.accept?.includes('video') ? 'Upload image or video' : 'Upload image'}</span>
                          <span className="text-[9px] text-white/20">{input.accept?.includes('video') ? 'Max 50MB, 8s for video' : 'JPG, PNG, WebP'}</span>
                        </button>
                      )}
                    </div>
                  )}

                  {input.type === 'text' && input.name === 'text_input' && shouldShowInput(input) && (
                    <textarea
                      value={textInput}
                      onChange={e => setTextInput(e.target.value)}
                      placeholder={input.description || 'Enter text...'}
                      rows={input.label?.toLowerCase().includes('script') || input.label?.toLowerCase().includes('description') ? 5 : 3}
                      className="w-full px-3 py-2 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/25 focus:outline-none focus:border-[#0EA5E9]/40 resize-none"
                      data-testid="text-input-1"
                    />
                  )}

                  {input.type === 'text' && input.name === 'text_input_2' && shouldShowInput(input) && (
                    <textarea
                      value={textInput2}
                      onChange={e => setTextInput2(e.target.value)}
                      placeholder={input.description || 'Enter text...'}
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/25 focus:outline-none focus:border-[#0EA5E9]/40 resize-none"
                      data-testid="text-input-2"
                    />
                  )}

                  {input.type === 'dropdown' && (
                    <div>
                      {/* Special visual dropdown for color grade styles */}
                      {slug === 'cinematic-color-grade' ? (
                        <div className="space-y-1.5 max-h-48 sm:max-h-64 overflow-y-auto pr-1 scrollbar-hide relative" data-testid="color-grade-visual-picker">
                          {(input.options || []).length > 4 && (
                            <div className="sticky bottom-0 z-10 pointer-events-none h-0">
                              <div className="absolute -bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-[#0B0F19]/60 to-transparent" />
                            </div>
                          )}
                          {(input.options || []).map(opt => {
                            const hint = COLOR_GRADE_HINTS[opt];
                            const isSelected = dropdownValue === opt;
                            return (
                              <button
                                key={opt}
                                onClick={() => setDropdownValue(opt)}
                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all ${
                                  isSelected ? 'bg-[#0EA5E9]/15 border-[#0EA5E9]/40' : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'
                                }`}
                                style={{ border: '1px solid' }}
                                data-testid={`color-grade-${opt.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                              >
                                {hint && (
                                  <div className="flex gap-0.5 flex-shrink-0">
                                    {hint.colors.map((c, ci) => (
                                      <div key={ci} className="w-4 h-4 rounded-sm" style={{ backgroundColor: c }} />
                                    ))}
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <span className={`text-xs block truncate ${isSelected ? 'text-white font-medium' : 'text-white/70'}`}>{opt}</span>
                                  {hint && <span className="text-[9px] text-white/30 block">{hint.hint}</span>}
                                </div>
                                {isSelected && <CheckCircle className="w-3.5 h-3.5 text-[#0EA5E9] flex-shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="relative">
                          <select
                            value={dropdownValue}
                            onChange={e => setDropdownValue(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-lg text-sm bg-white/[0.05] border border-white/[0.08] text-white focus:outline-none focus:border-[#0EA5E9]/40 appearance-none cursor-pointer"
                            data-testid="dropdown-input"
                          >
                            {(input.options || []).map(opt => (
                              <option key={opt} value={opt} className="bg-[#0B0F19] text-white">{opt}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
              )}
            </div>

            {/* Recent Runs — moved outside collapsible for mobile visibility */}
          </div>

          {/* Montage Timeline Editor (MOBILE / < lg) — sits in the input
              column immediately above Recent Runs so the user's natural flow
              after generating is (Generate → Timeline Editor → pick more
              clips from Recent Runs). On desktop this block is skipped;
              the wide-left-panel copy takes over. */}
          {isMontage && !isLgViewport && (
            <div className="px-4 py-3 border-t border-white/[0.06]" data-testid="montage-timeline-wrap">
              <MontageTimelinePanel token={token} runId={runResult?.run_id || null} />
            </div>
          )}

          {/* Recent Runs — always visible, outside collapsed inputs
              (but hidden in Focus Mode so the rail stays 48px).
              For AI Photoshop this becomes a tabbed panel with a sibling
              "Gallery" tab listing the user's okayed iterations across runs.
              We render the panel even when runHistory is empty for AI
              Photoshop, so users can still see / use their Gallery. */}
          {!focusModeActive && (runHistory.length > 0 || isAiPhotoshop) && (
            <div className="px-4 pb-4 pt-2 border-t border-white/[0.06]" data-testid="recent-runs-panel">
              {isAiPhotoshop && (
                <div className="flex items-center gap-1 mb-2 p-0.5 rounded-md bg-white/[0.03] border border-white/[0.05]" data-testid="ai-photoshop-tabs">
                  <button
                    onClick={() => setAiPhotoshopTab('runs')}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-semibold transition-all ${
                      aiPhotoshopTab === 'runs' ? 'bg-white/[0.08] text-white shadow-sm' : 'text-white/40 hover:text-white/70'
                    }`}
                    data-testid="ai-photoshop-tab-runs"
                  >
                    <History className="w-3 h-3" /> Recent Runs
                    {runHistory.length > 0 && (
                      <span className="text-[9px] px-1 py-0.5 rounded bg-white/[0.06] text-white/40">{runHistory.length}</span>
                    )}
                  </button>
                  <button
                    onClick={() => { setAiPhotoshopTab('gallery'); setGalleryRefreshKey(k => k + 1); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-semibold transition-all ${
                      aiPhotoshopTab === 'gallery' ? 'bg-yellow-500/15 text-yellow-300 shadow-sm ring-1 ring-yellow-500/30' : 'text-white/40 hover:text-white/70'
                    }`}
                    data-testid="ai-photoshop-tab-gallery"
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7.4L12 17l-6.3 4.4L8 14l-6-4.6h7.6z"/></svg>
                    Gallery
                  </button>
                </div>
              )}
              {isAiPhotoshop && aiPhotoshopTab === 'gallery' ? (
                <AIPhotoshopGalleryPanel
                  refreshKey={galleryRefreshKey}
                  onReopen={async (entry) => {
                    try {
                      const res = await axios.post(`${API}/api/ai-photoshop/gallery/${entry.id}/reopen`);
                      if (res.data?.shot_id) {
                        setPhotoshopShotId(res.data.shot_id);
                        setPhotoshopEditUrl(null);
                        // Switch back to the workbench view by clearing
                        // the runResult so the photoshopShotId render
                        // branch lights up.
                        setRunResult(null);
                        toast.success('Workbench re-opened with this image');
                      } else {
                        toast.error('Could not reopen entry');
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to reopen entry');
                    }
                  }}
                />
              ) : (runHistory.length > 0 && (
              <>
              <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="w-full flex items-center justify-between px-1 mb-2 group"
                >
                  <div className="flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5 text-white/30" />
                    <span className="text-[10px] font-mono uppercase tracking-wider text-white/40 group-hover:text-white/60">{isAiPhotoshop ? '' : 'Recent Runs'}</span>
                    {!isAiPhotoshop && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.06] text-white/30">{runHistory.length}</span>
                    )}
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-white/30 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                </button>
                {showHistory && (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto scrollbar-hide relative">
                    {runHistory.length > 3 && (
                      <div className="sticky top-0 z-10 pointer-events-none h-0">
                        <div className="absolute -top-1 left-0 right-0 h-3 bg-gradient-to-b from-[#0B0F19]/80 to-transparent" />
                      </div>
                    )}
                    {runHistory.map((run, i) => (
                      <button
                        key={run.run_id}
                        onClick={() => loadPreviousRun(run)}
                        className={`w-full p-2 rounded-lg text-left transition-all ${
                          runResult?.run_id === run.run_id ? 'bg-[#0EA5E9]/10 border-[#0EA5E9]/30' : 'bg-white/[0.02] border-white/[0.05] hover:bg-white/[0.05]'
                        }`}
                        style={{ border: '1px solid' }}
                        data-testid={`run-history-${i}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            {run.status === 'completed' ? <CheckCircle className="w-3 h-3 text-[#22C55E]" /> :
                             run.status === 'failed' ? <XCircle className="w-3 h-3 text-red-400" /> :
                             <Clock className="w-3 h-3 text-yellow-400" />}
                            <span className="text-[10px] text-white/50 capitalize">{run.status}</span>
                          </div>
                          <span className="text-[9px] text-white/25">{run.outputs?.length || 0} outputs</span>
                        </div>
                        <span className="text-[9px] text-white/20 block mt-0.5">{formatDistanceToNow(new Date(run.started_at), { addSuffix: true })}</span>
                        {/* Thumbnail strip */}
                        {run.status === 'completed' && run.outputs?.filter(o => o.type === 'image').length > 0 && (
                          <div className="flex gap-1 mt-1.5">
                            {run.outputs.filter(o => o.type === 'image').slice(0, 3).map((o, oi) => (
                              <SilentAuthImage key={oi} src={resolveOutputUrl(o.url)} className="w-8 h-6 rounded object-cover opacity-60" />
                            ))}
                          </div>
                        )}
                        {run.status === 'processing' && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <Loader2 className="w-3 h-3 text-teal-400 animate-spin" />
                            <span className="text-[9px] text-teal-400/70">{run.current_node || 'Generating...'}</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
              ))}
              </div>
            )}
          
          {/* Desktop Generate Button - Inside panel, only visible on lg+
              Hidden in Focus Mode so the 48px rail stays clean. */}
          <div className={`hidden lg:block p-4 border-t border-white/[0.06] ${focusModeActive ? '!hidden' : ''} ${(isUrlToAd || isSeedanceDirect || isHappyHorse || isKling) ? '!hidden' : ''}`}>
            <button
              onClick={runRecipe}
              disabled={isMontage ? (!canGenerate || (!slotsProcessed && (montageSlots.cast?.file || montageSlots.costume?.file || montageSlots.environment?.file || montageSlots.sku?.file))) : (running || !canGenerate)}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: ((isMontage ? false : running) || !canGenerate) ? 'rgba(14,165,233,0.2)' : 'linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)', boxShadow: ((isMontage ? false : running) || !canGenerate) ? 'none' : '0 4px 15px rgba(14, 165, 233, 0.3)' }}
              data-testid="generate-btn"
              title={!canGenerate ? 'Upload required inputs first' : (isAiPhotoshop ? 'Open the AI Photoshop workbench with this image + your prompt' : `Generate — ${recipe?.pricing?.credits || 0} credits`)}
            >
              {running && !isMontage ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {isAiPhotoshop ? 'Opening...' : 'Processing...'}</>
              ) : isAiPhotoshop ? (
                <><Sparkles className="w-4 h-4" /> Open in Workbench</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Generate{activeRuns.filter(r => r.status === 'processing').length > 0 ? ` (${activeRuns.filter(r => r.status === 'processing').length} running)` : ''}</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* FIXED BUTTONS - Outside all scroll containers for Mobile WebKit compatibility */}
      
      {/* Mobile bottom bar - shows either View Results OR Generate, not both */}
      <div className={`fixed bottom-0 left-0 right-0 lg:hidden z-30 border-t border-white/[0.06] ${(isUrlToAd || isSeedanceDirect || isHappyHorse || isKling) ? 'hidden' : ''}`} 
           style={{ background: 'rgba(11, 15, 25, 0.95)', backdropFilter: 'blur(8px)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="p-3 flex gap-2">
          {runResult && runResult.outputs?.length > 0 ? (
            <>
              {/* View Results — primary when results exist */}
              <button 
                onClick={() => {
                  const outputEl = document.querySelector('[data-testid="recipe-outputs"]');
                  if (outputEl) {
                    outputEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)' }}
                data-testid="view-results-btn"
              >
                <CheckCircle className="w-4 h-4" />
                View {runResult.outputs.length} Result{runResult.outputs.length > 1 ? 's' : ''}
              </button>
              {/* Generate Again — secondary */}
              <button
                onClick={runRecipe}
                disabled={running || !canGenerate}
                className="py-3 px-4 rounded-xl text-sm font-semibold text-white/70 disabled:opacity-30 flex items-center justify-center gap-2 border border-white/10"
                data-testid="generate-again-mobile"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </>
          ) : (
            /* Generate — primary when no results */
            <div className="flex-1 flex flex-col gap-2">
              {isMontage && !slotsProcessed && (montageSlots.cast?.file || montageSlots.costume?.file || montageSlots.environment?.file || montageSlots.sku?.file) ? (
                <button
                  onClick={processSlots}
                  disabled={processing}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                  style={{ background: processing ? 'rgba(168,85,247,0.2)' : 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)' }}
                  data-testid="process-btn-mobile"
                >
                  {processing ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <><Wand2 className="w-4 h-4" /> Process Images</>}
                </button>
              ) : (
              <button
                onClick={runRecipe}
                disabled={isMontage ? (!canGenerate || (!slotsProcessed && (montageSlots.cast?.file || montageSlots.costume?.file))) : (running || !canGenerate)}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: (running || !canGenerate) ? 'rgba(14,165,233,0.2)' : 'linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)', boxShadow: (running || !canGenerate) ? 'none' : '0 4px 15px rgba(14, 165, 233, 0.3)' }}
                data-testid="generate-btn-mobile"
              >
                {running && !isMontage ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {isAiPhotoshop ? 'Opening...' : 'Processing...'}</>
                ) : isAiPhotoshop ? (
                  <><Sparkles className="w-4 h-4" /> Open in Workbench</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Generate{activeRuns.filter(r => r.status === 'processing').length > 0 ? ` (${activeRuns.filter(r => r.status === 'processing').length} running)` : ''}</>
                )}
              </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => { setLightboxUrl(null); setZoomScale(1); setZoomPos({ x: 0, y: 0 }); }}
          onKeyDown={(e) => { if (e.key === 'ArrowLeft') lightboxPrev(); if (e.key === 'ArrowRight') lightboxNext(); if (e.key === 'Escape') { setLightboxUrl(null); setZoomScale(1); setZoomPos({ x: 0, y: 0 }); } }}
          tabIndex={0} ref={el => el && el.focus()}
          data-testid="lightbox">
          {/* Close button */}
          <button className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full z-10" onClick={() => { setLightboxUrl(null); setZoomScale(1); setZoomPos({ x: 0, y: 0 }); }}>
            <X className="w-6 h-6 text-white" />
          </button>
          
          {/* Counter */}
          {imageOutputUrls.length > 1 && (
            <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/50 text-xs text-white/70 z-10">
              {lightboxIndex + 1} / {imageOutputUrls.length}
            </div>
          )}

          {/* Zoom indicator */}
          {zoomScale > 1 && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 text-xs text-white/70 z-10" data-testid="lightbox-zoom-indicator">
              {Math.round(zoomScale * 100)}%
            </div>
          )}
          
          {/* Prev arrow */}
          {imageOutputUrls.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 p-2 hover:bg-white/10 rounded-full z-10 transition-colors"
              data-testid="lightbox-prev">
              <ChevronRight className="w-6 h-6 text-white rotate-180" />
            </button>
          )}
          
          {/* Image with pinch-to-zoom */}
          <div
            ref={zoomImgRef}
            className="max-w-full max-h-full overflow-hidden touch-none select-none"
            onTouchStart={handleLightboxTouchStart}
            onTouchMove={handleLightboxTouchMove}
            onTouchEnd={handleLightboxTouchEnd}
            onClick={e => e.stopPropagation()}
            data-testid="lightbox-zoom-container"
          >
            <AuthImage
              src={lightboxUrl}
              alt="Full size"
              className="max-w-full max-h-[80vh] object-contain rounded-lg transition-transform duration-100"
              style={{ transform: `scale(${zoomScale}) translate(${zoomPos.x / zoomScale}px, ${zoomPos.y / zoomScale}px)` }}
            />
          </div>
          
          {/* Next arrow */}
          {imageOutputUrls.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-white/10 rounded-full z-10 transition-colors"
              data-testid="lightbox-next">
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}
          
          {/* Bottom bar: zoom hint + download */}
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between z-10">
            <span className="text-[10px] text-white/30 hidden sm:hidden">
              {zoomScale > 1 ? 'Double-tap to reset' : 'Pinch or double-tap to zoom'}
            </span>
            <span className="text-[10px] text-white/30 sm:hidden">
              {zoomScale > 1 ? 'Double-tap to reset' : 'Pinch or double-tap to zoom'}
            </span>
            <button onClick={(e) => { e.stopPropagation(); authDownload(lightboxUrl, lightboxUrl.split('/').pop()); }}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors ml-auto">
              <Download className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      )}
      <RecipeAssistant currentRecipe={recipe} user={user} />
      <ImagePickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(file, dataUrl /* , metadata */) => {
          // Mirror what handleImageSelect does for a disk upload so the
          // downstream recipe flow is identical.
          setImageFile(file);
          setImagePreview(dataUrl);
          setVideoDuration(null);
          if (fileRef.current) fileRef.current.value = '';
          toast.success('Keyframe loaded — ready to edit');
        }}
        title="Pick a keyframe from your projects"
      />
    </div>
  );
}


// Silent thumbnail — hides completely on error (used in sidebar)
function SilentAuthImage({ src, className }) {
  const [blobUrl, setBlobUrl] = React.useState(null);
  const [hidden, setHidden] = React.useState(false);
  React.useEffect(() => {
    if (!src) { setHidden(true); return; }
    const token = localStorage.getItem('token');
    const isOwnApi = src.includes('/api/recipes/storage/') || (API && src.startsWith(API));
    const headers = (src.startsWith('http') && !isOwnApi) ? {} : { Authorization: `Bearer ${token}` };
    fetch(src, { headers })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => { if (blob.size > 0) setBlobUrl(URL.createObjectURL(blob)); else setHidden(true); })
      .catch(() => setHidden(true));
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [src]); // eslint-disable-line
  if (hidden || !blobUrl) return null;
  return <img src={blobUrl} alt="" className={className} />;
}


// ─── Expandable Text Output Component ───
function TextOutputCard({ output, i }) {
  const [expanded, setExpanded] = React.useState(false);
  const contentRef = React.useRef(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  React.useEffect(() => {
    if (contentRef.current) {
      setIsOverflowing(contentRef.current.scrollHeight > 160);
    }
  }, [output.content]);

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-white/40">{output.label}</span>
        <button 
          onClick={() => { navigator.clipboard.writeText(output.content); }}
          className="p-1 hover:bg-white/10 rounded transition-colors" data-testid={`copy-text-${i}`}>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/30"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
        </button>
      </div>
      <div 
        ref={contentRef}
        className={`text-xs text-white/70 overflow-hidden transition-all duration-300 prose prose-invert prose-xs prose-p:my-1 prose-headings:my-1 prose-li:my-0.5 prose-ul:my-1 prose-ol:my-1 ${expanded ? '' : 'max-h-40'}`}
      >
        <ReactMarkdown>{output.content}</ReactMarkdown>
      </div>
      {isOverflowing && (
        <button 
          onClick={() => setExpanded(!expanded)}
          className="text-[10px] text-[#0EA5E9] hover:text-[#38BDF8] mt-1.5 transition-colors"
          data-testid={`expand-text-${i}`}
        >
          {expanded ? 'Show less' : 'Show more...'}
        </button>
      )}
    </div>
  );
}


// ─── Before/After Comparison Slider ───
function CompareSlider({ beforeSrc, afterSrc, beforeLabel = "Before", afterLabel = "After" }) {
  const [position, setPosition] = React.useState(50);
  const containerRef = React.useRef(null);
  const dragging = React.useRef(false);

  const handleMove = React.useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPosition((x / rect.width) * 100);
  }, []);

  const onMouseMove = React.useCallback((e) => { if (dragging.current) handleMove(e.clientX); }, [handleMove]);
  const onTouchMove = React.useCallback((e) => { if (dragging.current) handleMove(e.touches[0].clientX); }, [handleMove]);
  const startDrag = React.useCallback(() => { dragging.current = true; }, []);
  const stopDrag = React.useCallback(() => { dragging.current = false; }, []);

  React.useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stopDrag);
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', stopDrag);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', stopDrag);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', stopDrag);
    };
  }, [onMouseMove, onTouchMove, stopDrag]);

  return (
    <div ref={containerRef} className="relative w-full aspect-[4/3] rounded-xl overflow-hidden select-none cursor-col-resize" data-testid="compare-slider">
      {/* After (full) */}
      <AuthImage src={afterSrc} alt={afterLabel} className="absolute inset-0 w-full h-full object-cover" />
      {/* Before (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${position}%` }}>
        <AuthImage src={beforeSrc} alt={beforeLabel} className="absolute inset-0 w-full h-full object-cover" style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%' }} />
      </div>
      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow-lg flex items-center justify-center">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0B0F19" strokeWidth="2"><path d="M8 3L3 8l5 5M16 3l5 5-5 5"/></svg>
        </div>
      </div>
      {/* Labels */}
      <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-[9px] font-bold bg-black/50 text-white/80 backdrop-blur-sm z-20">{beforeLabel}</span>
      <span className="absolute top-2 right-2 px-2 py-0.5 rounded text-[9px] font-bold bg-black/50 text-white/80 backdrop-blur-sm z-20">{afterLabel}</span>
    </div>
  );
}
